import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"
import { showSheetPreview, hideSheetPreview } from "../lib/sheet_preview"
import { MassTextRoom, MASS_TEXT_ROOM, massTextIcon } from "../lib/chatlog_mass_text"

// Toolbox Chat (builder) / Project Chat (owner) — the construction-module
// slide-over. Two screens like a phone chat app (Phelipe 2026-08-14): a
// chats LIST (rooms + previews + unread bubbles) and one CHAT (messages +
// composer) with a back arrow between them. Polling keeps it dead simple:
// ~5s while open, a slow unread check while closed. Sending is optimistic
// so it feels instant. The header walkie button lives outside this
// controller's DOM, so it talks to us via a window event.
export default class extends Controller {
  static targets = ["panel", "list", "empty", "panelTitle", "input", "sendButton",
                    "micButton", "speakButton", "speakRateButton", "voicePersona", "aiVoiceRow", "fileInput", "mentionMenu", "attachButton", "composerHint",
                    "recorder", "composerRow", "waveCanvas", "recTimer", "recDot",
                    "pauseButton", "micHint", "dragHandle",
                    "listScreen", "chatScreen", "chatList", "backButton", "brandTitle", "newChatButton",
                    "brandIcon", "chatIcon", "archiveButton", "archivedScreen", "archivedList", "archivedEmpty",
                    "archivedBar", "archivedBarUnarchive", "composerBox",
                    "confirmModal", "confirmTitle", "confirmMessage", "confirmButton", "roomInfoArchive",
                    "newRoomSheet", "newRoomName", "newRoomPeople",
                    "roomInfoSheet", "roomInfoAvatar", "roomInfoName", "iconGrid", "iconFileInput",
                    "roomInfoMembers", "roomInfoAddWrap", "roomInfoAddable"]

  static values = {
    projectId: Number,
    rooms: Array,
    currentUserId: Number,
    gc: Boolean,
    ackUrls: Object,
    layout: Object,
    settingsUrl: String,
    // Mass Text (Kloepfer K3-10): { enabled, i18n } for the builder's
    // Directory editors, {} for everyone else.
    massText: Object
  }

  // The panel can't shrink past this — composer + header stay usable.
  MIN_W = 320
  MIN_H = 400

  connect() {
    // Mass Text: the project Directory as a room (Kloepfer K3-10).
    this.mt = new MassTextRoom(this, this.massTextValue)
    const storedRoom = sessionStorage.getItem(this._roomKey())
    this.room = this._enterableRooms().includes(storedRoom) ? storedRoom
              : (this.roomsValue.includes("team") && this.gcValue ? "team" : this.roomsValue[0])
    // Which screen the panel wakes up on: the chat you were standing in,
    // otherwise the chats list — the list is the home screen.
    this.screen = sessionStorage.getItem(this._screenKey()) === "chat" ? "chat" : "list"
    this.roomsMeta = []
    this.archivedMeta = []
    this.lastId = 0
    this.open = false
    this.pendingAttachment = null
    this.mentions = new Map() // name → id, kept only while the text still carries the tag
    this.participants = []
    this.recorder = null

    this._onToggle = () => (this.open ? this.close() : this.openPanel())
    window.addEventListener("toolbox-chat:toggle", this._onToggle)
    // The purple "Ask AI" on Drawings and Specifications opens THIS Ask AI —
    // its answers link straight to the sheet (Larry K7-24, 2026-10-06; the old
    // panel there answered "as shown on C1" with no link).
    this._onAskAi = () => {
      if (!this.open) this.openPanel(false)
      this._enterRoom("ai", "Ask AI Opened From Page")
    }
    window.addEventListener("toolbox-chat:ask-ai", this._onAskAi)
    // The Directory card's "Message the directory" opens THIS panel on the
    // Mass Text room (Kloepfer K3-10).
    this._onMassText = () => {
      if (!this.mt.enabled) return
      if (!this.open) this.openPanel(false)
      this._enterRoom(MASS_TEXT_ROOM, "Mass Text Opened From Directory")
    }
    window.addEventListener("toolbox-chat:mass-text", this._onMassText)

    // Hover thumbnails on AI source rows — delegated, since bubbles are
    // rebuilt constantly (Larry 2026-08-25).
    this.element.addEventListener("mouseover", (e) => {
      const el = e.target.closest?.("[data-chat-thumb]")
      if (!el || el.contains(e.relatedTarget)) return
      showSheetPreview(el, el.dataset.pdfUrl, el.dataset.thumbLabel)
    })
    this.element.addEventListener("mouseout", (e) => {
      const el = e.target.closest?.("[data-chat-thumb]")
      if (!el || el.contains(e.relatedTarget)) return
      hideSheetPreview()
    })

    this._refreshUnread()
    this.closedTimer = setInterval(() => { if (!this.open) this._refreshUnread() }, 45000)

    // Keep the chat open across project tabs. With turbo-permanent the very
    // same panel DOM survives the visit — resume where it was. On a hard
    // reload the DOM starts fresh, so reopen and reload the room instead.
    if (sessionStorage.getItem(this._openKey()) === "1") {
      if (!this.panelTarget.classList.contains("hidden")) this._resume()
      else this.openPanel(false)
    }

    this._bindDropZone()

    // Paste (Larry 2026-08-20): a snipped screenshot or a file copied to the
    // clipboard lands in the chat like a drop. Document-level so it works
    // even when the composer isn't focused; guarded so pastes aimed at other
    // inputs on the page are left alone.
    this._onPaste = e => this._handlePaste(e)
    document.addEventListener("paste", this._onPaste)
  }

  async _handlePaste(e) {
    // Mass Text sends words only — texts and emails to outside people.
    if (!this.open || this.screen !== "chat" || this.room === MASS_TEXT_ROOM) return
    const files = [...(e.clipboardData?.files || [])]
    if (files.length === 0) return
    const inPanel = this.element.contains(e.target)
    if (!inPanel && e.target !== document.body) return
    e.preventDefault()
    for (const [i, file] of files.entries()) {
      const name = file.name || `pasted-${Date.now()}${files.length > 1 ? `-${i + 1}` : ""}.png`
      await this._uploadBlob(file, name, file.type, /^image\//.test(file.type) ? "image" : /^video\//.test(file.type) ? "video" : "file")
      // Like a multi-file drop: each extra file goes out as its own message;
      // a single paste stages with the composer so a caption can ride along.
      if (files.length > 1 && this.pendingAttachment) await this.send()
    }
    this.inputTarget?.focus()
  }

  // Drag a file anywhere onto the open panel to attach it. Inline highlight
  // only (house rule — no full-page overlays): the message area tints green
  // while a file hovers. One file stages with the composer so a caption can
  // ride along; several files each land as their own message.
  _bindDropZone() {
    const panel = this.panelTarget
    if (panel.dataset.dndBound) return // turbo-permanent: never bind twice
    panel.dataset.dndBound = "1"
    this.dragDepth = 0

    const hasFiles = e => [...(e.dataTransfer?.types || [])].includes("Files")
    const paint = on => {
      this.listTarget.classList.toggle("ring-2", on)
      this.listTarget.classList.toggle("ring-inset", on)
      this.listTarget.classList.toggle("ring-[#53c463]", on)
      this.listTarget.classList.toggle("bg-[#eefaf0]", on)
    }

    panel.addEventListener("dragenter", e => {
      if (!hasFiles(e) || this.room === MASS_TEXT_ROOM) return
      e.preventDefault()
      this.dragDepth++
      paint(true)
    })
    panel.addEventListener("dragover", e => { if (hasFiles(e)) e.preventDefault() })
    panel.addEventListener("dragleave", () => {
      if (--this.dragDepth <= 0) { this.dragDepth = 0; paint(false) }
    })
    panel.addEventListener("drop", async e => {
      if (!hasFiles(e)) return
      e.preventDefault()
      this.dragDepth = 0
      paint(false)
      if (this.room === MASS_TEXT_ROOM) return

      const files = [...e.dataTransfer.files]
      if (files.length === 0) return
      if (files.length === 1) {
        const file = files[0]
        await this._uploadBlob(file, file.name, file.type, /^image\//.test(file.type) ? "image" : /^video\//.test(file.type) ? "video" : "file")
        this.inputTarget?.focus()
        return
      }
      for (const file of files) {
        await this._uploadBlob(file, file.name, file.type, /^image\//.test(file.type) ? "image" : /^video\//.test(file.type) ? "video" : "file")
        if (this.pendingAttachment) await this.send()
      }
    })
  }

  disconnect() {
    window.removeEventListener("toolbox-chat:toggle", this._onToggle)
    window.removeEventListener("toolbox-chat:ask-ai", this._onAskAi)
    window.removeEventListener("toolbox-chat:mass-text", this._onMassText)
    if (this._onPaste) document.removeEventListener("paste", this._onPaste)
    clearInterval(this.closedTimer)
    clearInterval(this.openTimer)
    if (this.voiceState && this.voiceState !== "idle") this._teardownVoice()
  }

  // ── Open / close / rooms ────────────────────────────────────────

  openPanel(focus = true) {
    this.open = true
    sessionStorage.setItem(this._openKey(), "1")
    this.panelTarget.classList.remove("hidden")
    this.panelTarget.classList.add("flex")
    this._restorePosition()
    this._showScreen(this.screen)
    if (this.screen === "chat") {
      this._loadRoom()
      if (focus) setTimeout(() => this.inputTarget?.focus(), 60)
    } else {
      this._loadChatList()
    }
    this.openTimer = setInterval(() => this._tick(), 5000)
  }

  // One heartbeat for both screens: new messages in the open chat, fresh
  // previews + unread bubbles on the list.
  _tick() {
    if (!this.open || document.hidden) return
    if (this.screen === "chat") this._poll()
    else this._loadChatList()
  }

  // ── Screens (Phelipe 2026-08-14): list ⇄ chat ⇄ archived ─────────
  _showScreen(name) {
    this.screen = name
    sessionStorage.setItem(this._screenKey(), name)
    const inChat = name === "chat"
    const inArchived = name === "archived"
    this.listScreenTarget.classList.toggle("hidden", name !== "list")
    this.listScreenTarget.classList.toggle("flex", name === "list")
    this.chatScreenTarget.classList.toggle("hidden", !inChat)
    this.chatScreenTarget.classList.toggle("flex", inChat)
    if (this.hasArchivedScreenTarget) {
      this.archivedScreenTarget.classList.toggle("hidden", !inArchived)
      this.archivedScreenTarget.classList.toggle("flex", inArchived)
    }
    this.backButtonTarget.classList.toggle("hidden", name === "list")
    // The little green + and the archive box belong to the list screen only.
    if (this.hasNewChatButtonTarget) this.newChatButtonTarget.classList.toggle("hidden", name !== "list")
    if (this.hasArchiveButtonTarget) this.archiveButtonTarget.classList.toggle("hidden", name !== "list")
    // In a chat the header wears the chat's name AND its icon; on the
    // list, the ChatLog brand mark.
    if (this.hasBrandTitleTarget) this.brandTitleTarget.classList.toggle("hidden", inChat || inArchived)
    if (this.hasBrandIconTarget) {
      this.brandIconTarget.classList.toggle("hidden", inChat)
      this.brandIconTarget.classList.toggle("inline-flex", !inChat)
    }
    if (this.hasChatIconTarget) {
      this.chatIconTarget.classList.toggle("hidden", !inChat)
      this.chatIconTarget.classList.toggle("inline-flex", inChat)
    }
    if (this.hasPanelTitleTarget) {
      this.panelTitleTarget.classList.toggle("hidden", name === "list")
      this.panelTitleTarget.classList.toggle("inline-flex", name !== "list")
      if (inArchived) {
        this.panelTitleTarget.textContent = "Archived chats"
        this.panelTitleTarget.classList.remove("cursor-pointer", "hover:text-gray-900")
        this.panelTitleTarget.classList.add("cursor-default")
        this.panelTitleTarget.title = ""
      }
    }
  }

  openArchived() {
    this._showScreen("archived")
    this._loadChatList()
  }

  openChat(event) {
    this._enterRoom(event.currentTarget.dataset.room, "Chat Opened From List")
  }

  // Rooms this panel may stand in: the server's list, Ask AI, and Mass Text
  // for the people who have it (Kloepfer K3-10).
  _enterableRooms() {
    return [...this.roomsValue, "ai", ...(this.mt?.enabled ? [MASS_TEXT_ROOM] : [])]
  }

  _enterRoom(room, eventName) {
    if (!this._enterableRooms().includes(room)) return
    if (this.voiceState && this.voiceState !== "idle") this.cancelVoice()
    // Into Mass Text from outside: the room itself, not an old thread.
    if (room === MASS_TEXT_ROOM) this.mt.enterRoom()
    this.room = room
    sessionStorage.setItem(this._roomKey(), room)
    this._showScreen("chat")
    this._loadRoom()
    setTimeout(() => this.inputTarget?.focus(), 60)
    track(eventName, {
      project_id: this.projectIdValue,
      room_kind: room === "ai" ? "ai" : room.startsWith("room:") ? "custom" : room
    })
  }

  backToList(event) {
    event?.stopPropagation()
    // In a Mass Text thread the back arrow returns to the room.
    if (this.room === MASS_TEXT_ROOM && this.screen === "chat" && this.mt.thread) {
      this.mt.closeThread()
      return
    }
    if (this.voiceState && this.voiceState !== "idle") this.cancelVoice()
    if (this.hasRoomInfoSheetTarget) this.closeRoomInfo()
    this._showScreen("list")
    this._loadChatList()
  }

  // ── Chat info (Phelipe 2026-08-14): tap the group's name up top ────
  // Rename, emoji icon, and the people in the chat. Groups only — the
  // server refuses DMs and built-in rooms, and the title is only
  // clickable when this can actually open.
  ICONS = ["👷", "🏗️", "🧱", "🔨", "🛠️", "⚡", "🔧", "🪵", "🏠", "🏢", "🚧", "🦺",
           "📐", "📋", "💰", "🧾", "🚚", "🏆", "⭐", "📌", "🔥", "✅", "❗", "🎯"]

  async openRoomInfo(event) {
    event?.stopPropagation()
    if (this.room === "ai" || this.room === MASS_TEXT_ROOM || this.screen !== "chat") return

    try {
      const resp = await fetch(this._url(`room_info?room=${encodeURIComponent(this.room)}`),
                               { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!resp.ok) return
      this._paintRoomInfo(await resp.json())
      this.roomInfoSheetTarget.classList.remove("hidden")
      this.roomInfoSheetTarget.classList.add("flex")
    } catch { /* sheet just doesn't open */ }
  }

  closeRoomInfo() {
    this.roomInfoSheetTarget.classList.add("hidden")
    this.roomInfoSheetTarget.classList.remove("flex")
    this.iconGridTarget.classList.add("hidden")
    this.iconGridTarget.classList.remove("flex")
  }

  _paintRoomInfo(data) {
    this.roomInfoEditable = !!data.editable
    this.roomInfoBuiltin = !!data.builtin
    this.roomInfoIcon = data.icon || null
    if (data.icon_image_url) {
      this.roomInfoAvatarTarget.classList.add("overflow-hidden")
      const img = document.createElement("img")
      img.src = data.icon_image_url
      img.alt = ""
      img.className = "w-full h-full object-cover"
      this.roomInfoAvatarTarget.replaceChildren(img)
    } else if (!data.editable && data.avatar_url) {
      this.roomInfoAvatarTarget.textContent = ""
      this.roomInfoAvatarTarget.classList.add("overflow-hidden")
      const img = document.createElement("img")
      img.src = data.avatar_url
      img.alt = ""
      img.className = "w-full h-full object-cover"
      this.roomInfoAvatarTarget.replaceChildren(img)
    } else {
      this.roomInfoAvatarTarget.classList.remove("overflow-hidden")
      this.roomInfoAvatarTarget.textContent = data.icon || "👥"
    }
    this.roomInfoNameTarget.value = data.name || ""
    // Built-in rooms show their stock label as the placeholder — clearing
    // the field puts the original name back.
    this.roomInfoNameTarget.placeholder = data.placeholder || ""

    // Read-only rooms (DMs, built-ins for non-commanders): the name is a
    // label, not a field, the avatar isn't a picker, and there's no Save.
    const name = this.roomInfoNameTarget
    name.readOnly = !this.roomInfoEditable
    name.classList.toggle("border-gray-300", this.roomInfoEditable)
    name.classList.toggle("border-transparent", !this.roomInfoEditable)
    name.classList.toggle("bg-transparent", !this.roomInfoEditable)
    name.classList.toggle("font-semibold", !this.roomInfoEditable)
    const save = name.nextElementSibling
    if (save) save.classList.toggle("hidden", !this.roomInfoEditable)
    this.roomInfoAvatarTarget.classList.toggle("cursor-pointer", this.roomInfoEditable)
    this.roomInfoAvatarTarget.classList.toggle("hover:ring-2", this.roomInfoEditable)
    this.roomInfoAvatarTarget.classList.toggle("cursor-default", !this.roomInfoEditable)

    this.roomInfoMembersTarget.replaceChildren(...(data.members || []).map(m => {
      const row = document.createElement("div")
      row.className = "flex items-center gap-2 border border-gray-100 rounded-lg px-2.5 py-1.5"
      row.appendChild(this._personAvatar(m.avatar_url))
      const label = document.createElement("span")
      label.className = "flex-1 min-w-0 truncate"
      label.textContent = m.name + (m.you ? " (you)" : "")
      if (m.creator) {
        const star = document.createElement("span")
        star.className = "ml-1 text-[11px] text-gray-400"
        star.textContent = "· started the chat"
        label.appendChild(star)
      }
      row.appendChild(label)
      if (m.removable) {
        const btn = document.createElement("button")
        btn.type = "button"
        btn.className = "shrink-0 text-[12px] cursor-pointer " +
          (m.you ? "text-red-500 hover:text-red-700" : "text-gray-400 hover:text-red-600")
        btn.textContent = m.you ? "Leave" : "Remove"
        btn.dataset.userId = m.id
        btn.setAttribute("data-action", "click->toolbox-chat#removeRoomMember")
        row.appendChild(btn)
      }
      return row
    }))

    if (this.hasRoomInfoArchiveTarget) {
      const meta = this._roomMeta(this.room)
      this.roomInfoArchiveTarget.classList.toggle("hidden", !(meta?.can_manage && !meta?.archived))
    }

    const addable = data.addable || []
    this.roomInfoAddWrapTarget.classList.toggle("hidden", addable.length === 0)
    this.roomInfoAddableTarget.replaceChildren(...addable.map(u => {
      const row = document.createElement("div")
      row.className = "flex items-center gap-2 border border-gray-100 rounded-lg px-2.5 py-1.5"
      row.appendChild(this._personAvatar(u.avatar_url))
      const label = document.createElement("span")
      label.className = "flex-1 min-w-0 truncate"
      label.textContent = u.name
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "shrink-0 text-[12px] text-[#2fa04e] hover:text-[#238a40] font-medium cursor-pointer"
      btn.textContent = "Add"
      btn.dataset.userId = u.id
      btn.setAttribute("data-action", "click->toolbox-chat#addRoomMember")
      row.appendChild(label)
      row.appendChild(btn)
      return row
    }))
  }

  // Archived chats read-only: composer swaps for the archived bar.
  _paintComposerState() {
    if (this.room === MASS_TEXT_ROOM) { this.mt.paintComposerState(); return }
    const meta = this._roomMeta(this.room)
    const archived = !!meta?.archived
    if (this.hasComposerBoxTarget) this.composerBoxTarget.classList.toggle("hidden", archived)
    if (this.hasArchivedBarTarget) {
      this.archivedBarTarget.classList.toggle("hidden", !archived)
      this.archivedBarTarget.classList.toggle("flex", archived)
    }
    if (this.hasArchivedBarUnarchiveTarget) {
      this.archivedBarUnarchiveTarget.classList.toggle("hidden", !(archived && meta?.can_manage))
    }
  }

  // ── Confirm popup (house rule: never browser confirm()) ───────────
  _confirm({ title, message, label, danger }) {
    return new Promise(resolve => {
      this.confirmTitleTarget.textContent = title
      this.confirmMessageTarget.textContent = message
      this.confirmButtonTarget.textContent = label
      this.confirmButtonTarget.className = "px-3 py-1.5 text-[13px] font-medium text-white rounded-lg cursor-pointer " +
        (danger ? "bg-red-600 hover:bg-red-700" : "bg-[#2fa04e] hover:bg-[#3ba94a]")
      this._confirmResolve = resolve
      this.confirmModalTarget.classList.remove("hidden")
      this.confirmModalTarget.classList.add("flex")
    })
  }

  _closeConfirm(result) {
    this.confirmModalTarget.classList.add("hidden")
    this.confirmModalTarget.classList.remove("flex")
    const resolve = this._confirmResolve
    this._confirmResolve = null
    if (resolve) resolve(result)
  }

  confirmCancel() { this._closeConfirm(false) }
  confirmAccept() { this._closeConfirm(true) }
  stopConfirm(event) { event.stopPropagation() }

  // ── Archive / unarchive / delete (Phelipe 2026-08-14) ─────────────
  async archiveCurrent() {
    const ok = await this._confirm({
      title: "Archive this chat?",
      message: "The conversation and its files move to the archive and go read-only. You can unarchive it anytime — nothing is lost.",
      label: "Archive"
    })
    if (!ok) return
    if (await this._roomCall("archive_room", this.room)) {
      this.closeRoomInfo()
      this.backToList()
    }
  }

  async unarchiveCurrent() {
    if (await this._roomCall("unarchive_room", this.room)) {
      await this._loadChatList()
      this._paintComposerState()
      this._paintChatHeader()
    }
  }

  async unarchiveRow(event) {
    event.stopPropagation()
    const room = event.currentTarget.dataset.room
    if (await this._roomCall("unarchive_room", room)) this._loadChatList()
  }

  async deleteRow(event) {
    event.stopPropagation()
    const room = event.currentTarget.dataset.room
    const meta = this._roomMeta(room)
    const builtin = meta?.kind === "built_in"
    const ok = await this._confirm({
      title: "Delete this chat forever?",
      message: builtin
        ? "All of its history — messages, files, voice notes — will be permanently removed. The " + (meta?.label || "chat") + " chat will start over fresh and empty. This can't be undone."
        : "All of its history — messages, files, voice notes — will be permanently removed for everyone. This can't be undone.",
      label: "Delete forever",
      danger: true
    })
    if (!ok) return
    if (await this._roomCall("delete_room", room)) {
      if (this.room === room) this.room = this.roomsValue[0] || "project"
      this._loadChatList()
    }
  }

  async _roomCall(action, room) {
    try {
      const resp = await fetch(this._url(action), {
        method: "POST", headers: this._headers(), credentials: "same-origin",
        body: JSON.stringify({ room: room })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't update the chat.")
      return true
    } catch (error) {
      alert(error.message || "Couldn't update the chat.")
      return false
    }
  }

  // The header slot where the brand mark sits on the list screen: inside
  // a chat it shows THIS chat's face — uploaded photo, DM profile photo,
  // emoji, or the stock glyph.
  _paintChatIcon(meta) {
    if (!this.hasChatIconTarget) return
    const slot = this.chatIconTarget
    slot.className = "hidden w-6 h-6 rounded-full items-center justify-center shrink-0 text-[17px] leading-none overflow-hidden " +
      (this.room === "ai" ? "bg-violet-100 text-violet-600"
       : meta?.kind === "dm" ? "bg-sky-100 text-sky-700"
       : "bg-[#eefaf0] text-[#2fa04e]")
    if (this.screen === "chat") { slot.classList.remove("hidden"); slot.classList.add("inline-flex") }

    const url = meta?.icon_image_url || (meta?.kind === "dm" ? meta.avatar_url : null)
    if (url) {
      const img = document.createElement("img")
      img.src = url
      img.alt = ""
      img.className = "w-full h-full object-cover"
      slot.replaceChildren(img)
    } else if (meta?.icon) {
      slot.textContent = meta.icon
    } else if (this.room === "ai") {
      slot.innerHTML = '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 5.7L19.5 9l-5.7 1.8L12 16.5l-1.8-5.7L4.5 9l5.7-1.3z"/></svg>'
    } else if (meta?.kind === "dm") {
      slot.innerHTML = '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.1a7.5 7.5 0 0 1 15 0v.15H4.5z"/></svg>'
    } else {
      slot.innerHTML = '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M18 18.7a4 4 0 0 0-6-3.5m-6 3.5a7.5 7.5 0 0 1 9-3.5M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.3 2.3 0 1 1-4.6 0 2.3 2.3 0 0 1 4.6 0ZM7.6 10a2.3 2.3 0 1 1-4.6 0 2.3 2.3 0 0 1 4.6 0Z"/></svg>'
    }
  }

  _personAvatar(url) {
    const wrap = document.createElement("span")
    wrap.className = "w-6 h-6 rounded-full overflow-hidden bg-gray-200 shrink-0"
    if (url) {
      const img = document.createElement("img")
      img.src = url
      img.alt = ""
      img.className = "w-full h-full object-cover"
      wrap.appendChild(img)
    }
    return wrap
  }

  toggleIconGrid() {
    if (!this.roomInfoEditable) return
    const grid = this.iconGridTarget
    if (grid.childElementCount === 0) {
      const upload = document.createElement("button")
      upload.type = "button"
      upload.title = "Upload a photo"
      upload.className = "w-9 h-9 rounded-lg hover:bg-white cursor-pointer flex items-center justify-center text-gray-500"
      upload.innerHTML = '<svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6.8 7.2 8 5h8l1.2 2.2H20a1.5 1.5 0 0 1 1.5 1.5V18a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 18V8.7A1.5 1.5 0 0 1 4 7.2h2.8ZM15.5 13a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"/></svg>'
      upload.setAttribute("data-action", "click->toolbox-chat#pickIconFile")
      grid.replaceChildren(upload, ...this.ICONS.map(glyph => {
        const b = document.createElement("button")
        b.type = "button"
        b.className = "w-9 h-9 rounded-lg text-[20px] hover:bg-white cursor-pointer"
        b.textContent = glyph
        b.setAttribute("data-action", "click->toolbox-chat#pickIcon")
        return b
      }))
    }
    grid.classList.toggle("hidden")
    grid.classList.toggle("flex")
  }

  pickIcon(event) {
    this.roomInfoIcon = event.currentTarget.textContent
    this.roomInfoAvatarTarget.classList.remove("overflow-hidden")
    this.roomInfoAvatarTarget.replaceChildren()
    this.roomInfoAvatarTarget.textContent = this.roomInfoIcon
    this.iconGridTarget.classList.add("hidden")
    this.iconGridTarget.classList.remove("flex")
    this.saveRoomInfo()
  }

  pickIconFile(event) {
    event?.preventDefault()
    this.iconFileInputTarget.click()
  }

  async iconFileChosen() {
    const file = this.iconFileInputTarget.files?.[0]
    this.iconFileInputTarget.value = ""
    if (!file) return
    if (!/^image\//.test(file.type)) { alert("Pick an image file."); return }

    try {
      const presign = await fetch(this._url("presign_uploads"), {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      if (!presign.ok) throw new Error((await presign.json().catch(() => ({}))).error || "Upload refused.")
      const upload = (await presign.json()).uploads[0]
      const put = await fetch(upload.upload_url, { method: "PUT", headers: { "Content-Type": upload.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed.")

      const resp = await fetch(this._url("update_room"), {
        method: "POST", headers: this._headers(), credentials: "same-origin",
        body: JSON.stringify({ room: this.room, icon_cache_name: upload.cache_name })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't save the photo.")

      const meta = this.roomsMeta.find(r => r.slug === this.room)
      if (meta) { meta.icon = data.icon; meta.icon_image_url = data.icon_image_url }
      this.roomInfoIcon = null
      this._paintChatHeader()
      this.openRoomInfo() // repaint the sheet with the new photo
    } catch (error) {
      alert(error.message || "Couldn't save the photo.")
    } finally {
      this.iconGridTarget.classList.add("hidden")
      this.iconGridTarget.classList.remove("flex")
    }
  }

  async saveRoomInfo(event) {
    event?.preventDefault()
    if (!this.roomInfoEditable) return
    const name = this.roomInfoNameTarget.value.trim()
    // Groups need a name; built-ins may go blank (falls back to stock).
    if (!name && !this.roomInfoBuiltin) { this.roomInfoNameTarget.focus(); return }
    try {
      const resp = await fetch(this._url("update_room"), {
        method: "POST", headers: this._headers(), credentials: "same-origin",
        body: JSON.stringify({ room: this.room, name: name, icon: this.roomInfoIcon || "" })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't save.")
      const meta = this.roomsMeta.find(r => r.slug === this.room)
      if (meta) { meta.label = data.label; meta.icon = data.icon; meta.icon_image_url = data.icon_image_url }
      this._paintChatHeader()
    } catch (error) {
      alert(error.message || "Couldn't save.")
    }
  }

  async addRoomMember(event) {
    await this._roomMemberCall("add_room_member", event.currentTarget.dataset.userId)
  }

  async removeRoomMember(event) {
    const btn = event.currentTarget
    // Leaving asks once, inline (house rule: no browser confirm()):
    // first tap arms the button, second tap goes through.
    if (btn.textContent === "Leave") {
      btn.textContent = "Leave — sure?"
      btn.classList.add("font-semibold")
      setTimeout(() => { if (btn.isConnected) { btn.textContent = "Leave"; btn.classList.remove("font-semibold") } }, 3000)
      return
    }
    const left = await this._roomMemberCall("remove_room_member", btn.dataset.userId)
    if (left) { this.closeRoomInfo(); this.backToList() }
  }

  async _roomMemberCall(action, userId) {
    try {
      const resp = await fetch(this._url(action), {
        method: "POST", headers: this._headers(), credentials: "same-origin",
        body: JSON.stringify({ room: this.room, user_id: userId })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't update the chat.")
      if (!data.left) this.openRoomInfo() // repaint members + addable
      return !!data.left
    } catch (error) {
      alert(error.message || "Couldn't update the chat.")
      return false
    }
  }

  // ── The chats list itself ─────────────────────────────────────────
  async _loadChatList() {
    try {
      const resp = await fetch(this._url("rooms"), { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!resp.ok) return
      const data = await resp.json()
      this.roomsMeta = data.rooms || []
      this.archivedMeta = data.archived || []
      this.roomsValue = [...new Set([...this.roomsValue,
        ...this.roomsMeta.map(r => r.slug), ...this.archivedMeta.map(r => r.slug)])]

      // S3 photo links come back freshly signed on every poll — same
      // image, different querystring. Pin the first URL we saw per image
      // (they stay valid ~7 days) so the browser can actually cache it
      // instead of re-downloading — that re-download was the icon
      // "blinking" (Phelipe 2026-08-17).
      this._pinnedUrls ||= {}
      const pin = (url) => {
        if (!url) return url
        const base = url.split("?")[0]
        this._pinnedUrls[base] ||= url
        return this._pinnedUrls[base]
      }
      const stabilize = (r) => {
        r.icon_image_url = pin(r.icon_image_url)
        r.avatar_url = pin(r.avatar_url)
      }
      this.roomsMeta.forEach(stabilize)
      this.archivedMeta.forEach(stabilize)

      // Nothing changed since the last paint → leave the DOM alone. The
      // 5s heartbeat was rebuilding every row (and its images) even when
      // idle, which also reset hover states mid-mouse-over.
      const snapshot = JSON.stringify([this.roomsMeta, this.archivedMeta])
      if (snapshot === this._listSnapshot) return
      this._listSnapshot = snapshot

      this._paintChatList()
      this._paintArchived()
    } catch { /* next tick retries */ }
  }

  _roomMeta(slug) {
    return this.roomsMeta.find(r => r.slug === slug) || this.archivedMeta.find(r => r.slug === slug)
  }

  _paintArchived() {
    if (!this.hasArchivedListTarget) return
    this.archivedListTarget.replaceChildren(...this.archivedMeta.map(meta => {
      const wrap = document.createElement("div")
      wrap.className = "flex items-center gap-1.5 mb-1.5"
      const row = this._chatRow(meta)
      row.classList.remove("mb-1.5")
      row.classList.add("flex-1", "min-w-0", "opacity-80")
      wrap.appendChild(row)

      if (meta.can_manage) {
        const un = document.createElement("button")
        un.type = "button"
        un.title = "Unarchive"
        un.dataset.room = meta.slug
        un.className = "shrink-0 w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-500 hover:text-[#2fa04e] hover:border-[#a7dcb4] cursor-pointer flex items-center justify-center"
        un.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 12a9 9 0 1 0 3-6.7M3 4v4h4"/></svg>'
        un.setAttribute("data-action", "click->toolbox-chat#unarchiveRow")
        wrap.appendChild(un)

        const del = document.createElement("button")
        del.type = "button"
        del.title = "Delete forever"
        del.dataset.room = meta.slug
        del.className = "shrink-0 w-8 h-8 rounded-lg bg-white border border-gray-200 text-gray-400 hover:text-red-600 hover:border-red-200 cursor-pointer flex items-center justify-center"
        del.innerHTML = '<svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m14.7 9-.3 9m-4.8 0-.3-9m9.9-3.2.9.1M4.8 5.8l.9-.1m0 0a48 48 0 0 1 12.6 0m-9.9 0V4.5A1.9 1.9 0 0 1 10.3 2.6h3.4a1.9 1.9 0 0 1 1.9 1.9v1.3m-9.9 0h9.9m2.7.2-.9 12a2.3 2.3 0 0 1-2.2 2.1H8.8a2.3 2.3 0 0 1-2.2-2.1l-.9-12"/></svg>'
        del.setAttribute("data-action", "click->toolbox-chat#deleteRow")
        wrap.appendChild(del)
      }
      return wrap
    }))
    if (this.hasArchivedEmptyTarget) this.archivedEmptyTarget.classList.toggle("hidden", this.archivedMeta.length > 0)
  }

  _paintChatList() {
    if (!this.hasChatListTarget) return
    const rows = []
    // Ask AI rides pinned at the top — it's a helper, not a conversation.
    rows.push(this._chatRow({
      slug: "ai", label: "Ask AI", kind: "ai",
      preview: "Ask about this project — reports, schedule, documents…",
      unread: 0, last_label: null, last_from: null
    }))
    this.roomsMeta.forEach(r => rows.push(this._chatRow(r)))
    this.chatListTarget.replaceChildren(...rows)
  }

  _chatRow(meta) {
    const row = document.createElement("button")
    row.type = "button"
    row.dataset.room = meta.slug
    row.className = "js-chat-row w-full flex items-center gap-3 px-3 py-2.5 mb-1.5 bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer text-left"
    row.setAttribute("data-action", "click->toolbox-chat#openChat")

    const avatar = document.createElement("span")
    avatar.className = "w-10 h-10 rounded-full flex items-center justify-center shrink-0 " +
      (meta.slug === "ai" ? "bg-violet-100 text-violet-600"
       : meta.kind === "dm" ? "bg-sky-100 text-sky-700"
       : "bg-[#eefaf0] text-[#2fa04e]")
    if (meta.icon_image_url) {
      avatar.classList.add("overflow-hidden")
      const img = document.createElement("img")
      img.src = meta.icon_image_url
      img.alt = ""
      img.className = "w-full h-full object-cover"
      avatar.appendChild(img)
    } else if (meta.kind === "dm" && meta.avatar_url) {
      avatar.classList.add("overflow-hidden")
      const img = document.createElement("img")
      img.src = meta.avatar_url
      img.alt = ""
      img.className = "w-full h-full object-cover"
      avatar.appendChild(img)
    } else if (meta.icon) {
      avatar.classList.add("text-[20px]")
      avatar.textContent = meta.icon
      // fall through skipped — emoji instead of the stock glyph
    } else avatar.innerHTML = meta.slug === "ai"
      ? '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 5.7L19.5 9l-5.7 1.8L12 16.5l-1.8-5.7L4.5 9l5.7-1.3z"/><path d="M19 14l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9z" opacity=".7"/></svg>'
      : meta.kind === "mass_text"
      ? massTextIcon()
      : meta.kind === "dm"
      ? '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.1a7.5 7.5 0 0 1 15 0v.15H4.5z"/></svg>'
      : '<svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path stroke-linecap="round" stroke-linejoin="round" d="M18 18.7a4 4 0 0 0-6-3.5m-6 3.5a7.5 7.5 0 0 1 9-3.5M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.3 2.3 0 1 1-4.6 0 2.3 2.3 0 0 1 4.6 0ZM7.6 10a2.3 2.3 0 1 1-4.6 0 2.3 2.3 0 0 1 4.6 0Z"/></svg>'

    const middle = document.createElement("span")
    middle.className = "flex-1 min-w-0"
    const unread = meta.unread || 0
    const nameCls = unread > 0 ? "text-gray-900" : "text-gray-800"
    const prevCls = unread > 0 ? "text-gray-700 font-medium" : "text-gray-400"
    const from = meta.last_from ? `${this._esc(meta.last_from)}: ` : ""
    middle.innerHTML =
      `<span class="block text-[13.5px] font-semibold ${nameCls} truncate">${this._esc(meta.label || "")}</span>` +
      `<span class="block text-[12.5px] ${prevCls} truncate">${meta.preview ? from + this._esc(meta.preview) : "No messages yet"}</span>`

    const right = document.createElement("span")
    right.className = "flex flex-col items-end gap-1 shrink-0"
    right.innerHTML =
      `<span class="text-[11px] ${unread > 0 ? "text-[#2fa04e] font-semibold" : "text-gray-400"}">${this._esc(meta.last_label || "")}</span>` +
      `<span class="js-row-badge ${unread > 0 ? "" : "hidden "}min-w-[20px] h-5 px-1.5 rounded-full bg-[#2fa04e] text-white text-[11px] font-bold flex items-center justify-center">${unread > 99 ? "99+" : unread}</span>`

    row.append(avatar, middle, right)
    return row
  }

  // ── Custom groups + DMs (Larry 2026-08-12) ─────────────────────────
  async openNewRoom() {
    this.newRoomSheetTarget.classList.remove("hidden")
    this.newRoomSheetTarget.classList.add("flex")
    this.newRoomNameTarget.value = ""
    try {
      const resp = await fetch(this._url("people"), { headers: { Accept: "application/json" }, credentials: "same-origin" })
      const data = await resp.json().catch(() => ({}))
      // Grouped by company, own company first (Phelipe 2026-08-20) — a bare
      // list of names doesn't tell you whether you're about to message the
      // owner, the GC or a sub.
      const groups = data.companies || []
      this.newRoomPeopleTarget.innerHTML = groups.length
        ? groups.map(g => `<div class="pt-1 first:pt-0">
            <p class="text-[13px] font-semibold text-gray-500 mb-0.5">${this._esc(g.company)}</p>
            ${g.people.map(p => `<label class="flex items-center gap-2 py-0.5 pl-2 cursor-pointer">
              <input type="checkbox" value="${p.id}" class="js-room-person rounded border-gray-300 text-[#53c463] focus:ring-[#53c463]">
              <span>${this._esc(p.name)}</span></label>`).join("")}
          </div>`).join("")
        : `<p class="text-gray-400">Nobody else to add here yet.</p>`
    } catch {
      this.newRoomPeopleTarget.innerHTML = `<p class="text-red-500">Couldn't load people — try again.</p>`
    }
  }

  closeNewRoom() {
    this.newRoomSheetTarget.classList.add("hidden")
    this.newRoomSheetTarget.classList.remove("flex")
  }

  async createRoom() {
    const ids = [...this.newRoomPeopleTarget.querySelectorAll(".js-room-person:checked")].map(b => b.value)
    if (ids.length === 0) return
    const name = this.newRoomNameTarget.value.trim()
    // No name + exactly one person = a direct message; otherwise a group.
    const payload = (!name && ids.length === 1) ? { user_id: ids[0] } : { name: name, member_ids: ids }
    if (!payload.user_id && !payload.name) {
      this.newRoomNameTarget.focus()
      return
    }

    try {
      const resp = await fetch(this._url("create_room"), {
        method: "POST", headers: this._headers(), credentials: "same-origin",
        body: JSON.stringify(payload)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't start the chat.")
      if (!this.roomsValue.includes(data.room.slug)) this.roomsValue = [...this.roomsValue, data.room.slug]
      this.closeNewRoom()
      // Straight into the fresh chat, like tapping the person on your phone.
      this.room = data.room.slug
      sessionStorage.setItem(this._roomKey(), this.room)
      this._showScreen("chat")
      this._loadRoom()
      // The fresh room isn't in roomsMeta yet, so the header just painted
      // the generic group look — a DM should wear the other person's face
      // and name right away, not only after leaving and coming back
      // (Phelipe 2026-08-20). Fetch the list and repaint.
      this._loadChatList().then(() => this._paintChatHeader())
    } catch (error) {
      alert(error.message || "Couldn't start the chat.")
    }
  }

  // ── Movable panel (Larry 2026-08-12: "so it's out of your work area") ──
  // Dragging the header floats the panel as a window; the position sticks
  // for the session (per project); double-click docks it back to the right
  // edge. Buttons in the header still click normally — a drag only starts
  // after a few pixels of travel.
  dragStart(event) {
    if (event.target.closest("button, a, input")) return
    const startX = event.clientX
    const startY = event.clientY
    const rect = this.panelTarget.getBoundingClientRect()
    let moved = false

    const onMove = (e) => {
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      if (!moved && Math.abs(dx) + Math.abs(dy) < 5) return
      moved = true
      this._floatAt(rect.left + dx, rect.top + dy, rect.height, rect.width)
    }
    const onUp = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      if (moved) this._savePosition()
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    event.preventDefault()
  }

  // ── Resize (Phelipe 2026-08-14, user request): drag any edge or a
  // bottom corner. Floats the panel first so width/height are free, and
  // holds the 320×400 minimum.
  resizeStart(event) {
    const dir = event.currentTarget.dataset.resizeDir
    const rect = this.panelTarget.getBoundingClientRect()
    const startX = event.clientX
    const startY = event.clientY
    // Docked panels float in place before their first resize.
    this._floatAt(rect.left, rect.top, rect.height, rect.width)
    let moved = false

    const onMove = (e) => {
      moved = true
      const dx = e.clientX - startX
      const dy = e.clientY - startY
      let { left, top, width, height } = rect

      if (dir.includes("l")) {
        width = Math.max(this.MIN_W, Math.min(width - dx, e.clientX >= 0 ? rect.left + rect.width - 8 : width))
        left = rect.left + (rect.width - width)
      }
      if (dir.includes("r")) width = Math.max(this.MIN_W, width + dx)
      if (dir.includes("b")) height = Math.max(this.MIN_H, height + dy)

      width = Math.min(width, window.innerWidth - 16)
      height = Math.min(height, window.innerHeight - top - 8)

      const p = this.panelTarget
      p.style.left = `${Math.max(8, left)}px`
      p.style.top = `${top}px`
      p.style.width = `${width}px`
      p.style.height = `${height}px`
    }
    const onUp = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      if (moved) this._savePosition()
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    event.preventDefault()
    event.stopPropagation()
  }

  resetPosition() {
    const p = this.panelTarget
    p.classList.add("inset-y-0", "right-0")
    p.classList.remove("rounded-xl", "border")
    p.style.top = p.style.left = p.style.height = p.style.width = ""
    sessionStorage.removeItem(this._posKey())
    this.layoutValue = {}
    this._pushLayout(null)
  }

  _floatAt(left, top, height, width) {
    const p = this.panelTarget
    const w = Math.max(this.MIN_W, Math.min(width || p.offsetWidth || 410, window.innerWidth - 16))
    const clampedLeft = Math.min(Math.max(left, 8), window.innerWidth - w - 8)
    const clampedTop = Math.min(Math.max(top, 8), window.innerHeight - 120)
    p.classList.remove("inset-y-0", "right-0")
    p.classList.add("rounded-xl", "border", "border-gray-200")
    p.style.left = `${clampedLeft}px`
    p.style.top = `${clampedTop}px`
    p.style.width = `${w}px`
    p.style.height = `${Math.max(this.MIN_H, Math.min(height || p.offsetHeight, window.innerHeight - clampedTop - 8))}px`
  }

  _savePosition() {
    const p = this.panelTarget
    const layout = { left: parseInt(p.style.left) || 0, top: parseInt(p.style.top) || 0,
                     width: p.offsetWidth, height: p.offsetHeight }
    try {
      sessionStorage.setItem(this._posKey(), JSON.stringify(layout))
    } catch { /* storage full — position just doesn't stick */ }
    // Per-user (Phelipe 2026-08-14): the window reopens exactly here on the
    // next login too. Debounced so a long drag is one write.
    this.layoutValue = layout
    clearTimeout(this._layoutTimer)
    this._layoutTimer = setTimeout(() => this._pushLayout(layout), 600)
  }

  _pushLayout(layout) {
    if (!this.settingsUrlValue) return
    fetch(this.settingsUrlValue, {
      method: "PUT", headers: this._headers(), credentials: "same-origin",
      body: JSON.stringify({ setting: { chat_panel_layout: layout } })
    }).catch(() => { /* keeps the session copy; next move retries */ })
  }

  _restorePosition() {
    let saved = null
    try { saved = JSON.parse(sessionStorage.getItem(this._posKey()) || "null") } catch { /* corrupt */ }
    if (!saved?.width && this.layoutValue?.width) saved = this.layoutValue
    if (!saved?.width) return
    this._floatAt(parseInt(saved.left) || 0, parseInt(saved.top) || 0,
                  parseInt(saved.height) || null, parseInt(saved.width) || null)
  }

  _posKey() {
    return `toolbox_chat_pos_${this.projectIdValue}`
  }

  // The panel arrived from the previous page via turbo-permanent, already
  // open and full of messages — pick up where it left off without repainting.
  _resume() {
    this.open = true
    this.lastId = Math.max(0, ...[...this.listTarget.querySelectorAll("[data-mid]")]
      .map(el => parseInt(el.dataset.mid) || 0))
    this._showScreen(this.screen)
    if (this.screen === "chat") this._paintChatHeader()
    else this._loadChatList()
    clearInterval(this.openTimer)
    this.openTimer = setInterval(() => this._tick(), 5000)
    this._tick()
    // Controller state died with the old page; the mention list didn't ride
    // along in the DOM, so quietly refill it. (Mass Text has no tagging.)
    if (this.participants.length === 0 && this.room !== MASS_TEXT_ROOM) this._refillParticipants()
  }

  async _refillParticipants() {
    try {
      const resp = await fetch(this._url(`messages?room=${this.room}`), { headers: { Accept: "application/json" } })
      if (resp.ok) this.participants = (await resp.json()).participants || []
    } catch { /* mention picker just stays empty until the next open */ }
  }

  close() {
    this.open = false
    if (this._aiRec) this._aiTalkStop(false)
    this._stopSpeakback()
    sessionStorage.removeItem(this._openKey())
    this.panelTarget.classList.add("hidden")
    this.panelTarget.classList.remove("flex")
    clearInterval(this.openTimer)
    this._refreshUnread()
  }

  _openKey() { return `chatlog-open-${this.projectIdValue}` }
  _roomKey() { return `chatlog-room-${this.projectIdValue}` }
  _screenKey() { return `chatlog-screen-${this.projectIdValue}` }

  _paintChatHeader() {
    // The header wears the name of the chat you're standing in. For a
    // GROUP the name is a live button (chat info); everywhere else it's
    // just a label. Mass Text paints its own: the room's reach, or the
    // person in a thread.
    const mt = this.room === MASS_TEXT_ROOM
    if (mt) this.mt.paintHeader()
    else if (this.hasPanelTitleTarget) {
      const meta = this._roomMeta(this.room)
      const label = this.room === "ai" ? "Ask AI"
        : meta?.label
        || (this.room === "team" ? (this.gcValue ? "My Team" : "Team")
            : this.room === "project" ? "Project Chat" : "Chat")
      const clickable = this.room !== "ai"
      this.panelTitleTarget.textContent = label
      this._paintChatIcon(meta)
      if (clickable) this.panelTitleTarget.insertAdjacentHTML("beforeend",
        '<svg class="w-3 h-3 shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5"/></svg>')
      this.panelTitleTarget.classList.toggle("cursor-pointer", clickable)
      this.panelTitleTarget.classList.toggle("hover:text-gray-900", clickable)
      this.panelTitleTarget.classList.toggle("cursor-default", !clickable)
      this.panelTitleTarget.title = clickable
        ? (meta?.kind === "group" ? "Chat name, icon and people" : "Who's in this chat")
        : ""
    }

    // Ask AI: no files, no tagging. The mic is back (Phelipe 2026-08-26) —
    // in this room it means "ask out loud", not a voice note — and spoken
    // answers come from a natural Gemini voice, not the browser robot that
    // got the 08-21 version pulled.
    const ai = this.room === "ai"
    if (this.hasMicButtonTarget) {
      // Firefox has no SpeechRecognition — the AI room's mic hides there.
      const srAvailable = !!(window.SpeechRecognition || window.webkitSpeechRecognition)
      this.micButtonTarget.classList.toggle("hidden", ai && !srAvailable)
      if (ai) this._paintAiTalk(false)
      else this.micButtonTarget.className =
        "w-9 h-9 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 cursor-pointer flex items-center justify-center shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2fa04e]/40 transition-colors"
      // Mass Text sends words only: no voice notes, no files.
      if (mt) this.micButtonTarget.classList.add("hidden")
      this.micButtonTarget.title = ai ? "Ask out loud" : "Record a voice note"
    }
    if (this.hasSpeakButtonTarget) {
      this.speakButtonTarget.classList.toggle("hidden", !ai)
      if (ai) this._paintSpeakButton()
    }
    this._paintSpeakRate()
    this._paintVoicePersona()
    // In the AI room the voice controls sit in their own row UNDER the
    // input so the question box keeps full width (Phelipe 2026-08-26); the
    // same button elements move back beside the input everywhere else.
    if (this.hasAiVoiceRowTarget && this.hasInputTarget) {
      const controls = [
        this.hasMicButtonTarget && this.micButtonTarget,
        this.hasSpeakButtonTarget && this.speakButtonTarget,
        this.hasSpeakRateButtonTarget && this.speakRateButtonTarget,
        this.hasVoicePersonaTarget && this.voicePersonaTarget
      ].filter(Boolean)
      this.aiVoiceRowTarget.classList.toggle("hidden", !ai)
      this.aiVoiceRowTarget.classList.toggle("flex", ai)
      if (ai) controls.forEach(el => this.aiVoiceRowTarget.appendChild(el))
      else controls.forEach(el => this.inputTarget.parentElement.insertBefore(el, this.inputTarget))
    }
    if (this.hasAttachButtonTarget) this.attachButtonTarget.classList.toggle("hidden", ai || mt)
    if (this.hasInputTarget) {
      this.inputTarget.placeholder = ai ? "Ask about this project…" : "Message…"
    }
    if (this.hasComposerHintTarget) {
      this.composerHintTarget.textContent = ai
        ? "Answers come from this project's reports, schedule, action items, documents, plans and specs. Finance data is off limits. Tap the mic to ask out loud."
        : "Type @ to tag someone — they get a badge. Tap the envelope on your message to also email them."
    }
    if (ai || mt) this._clearAttachment()
    if (mt) this.mt.paintComposerFromCache()
    else this.mt.leave()
  }

  async _loadRoom() {
    // Leaving a room mid-listen or mid-speech kills both cleanly (the 08-21
    // teardown leftovers broke navigation twice — one chokepoint this time).
    if (this._aiRec) this._aiTalkStop(false)
    this._stopSpeakback()
    this._paintChatHeader()
    this._paintComposerState()
    // Fresh session straight into a chat: the list never loaded, so the
    // header can't know a custom room's name yet — fetch it quietly.
    if (this.roomsMeta.length === 0 && this.room.startsWith("room:")) {
      this._loadChatList().then(() => this._paintChatHeader())
    }
    this.listTarget.innerHTML = ""
    this.lastId = 0
    if (this.room === "ai") { this._renderAiThread(); return }
    if (this.room === MASS_TEXT_ROOM) { this.emptyTarget.classList.add("hidden"); this.mt.load(); return }
    this.emptyTarget.textContent = "No messages yet — say hi to your team."
    const room = this.room
    try {
      const resp = await fetch(this._url(`messages?room=${room}`), { headers: { Accept: "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      // Moved to another room while this was loading — never paint it there.
      if (room !== this.room) return
      this.participants = data.participants || []
      this.receipts = data.receipts || null
      this._renderMessages(data.messages || [], data.last_read_at)
      this._markRead()
    } catch { /* next poll retries */ }
  }

  async _poll() {
    if (!this.open || document.hidden || this.room === "ai") return
    if (this.room === MASS_TEXT_ROOM) return this.mt.poll()
    const room = this.room
    try {
      const resp = await fetch(this._url(`messages?room=${room}&after_id=${this.lastId}`),
                               { headers: { Accept: "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      if (room !== this.room) return
      // Ticks can move without any new message — someone just read the room.
      if (data.receipts) { this.receipts = data.receipts; this._paintTicks() }
      const fresh = (data.messages || []).filter(m => !this.listTarget.querySelector(`[data-mid="${m.id}"]`))
      if (fresh.length) {
        fresh.forEach(m => this.listTarget.appendChild(this._bubble(m)))
        this._scrollDown()
        this._markRead()
      }
    } catch { /* transient */ }
  }

  async _markRead() {
    try {
      await fetch(this._url("mark_read"), {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ room: this.room })
      })
      this._zeroBadge(this.room)
    } catch { /* fine */ }
  }

  // ── Unread badges ───────────────────────────────────────────────

  async _refreshUnread() {
    try {
      const resp = await fetch(this._url("unread"), { headers: { Accept: "application/json" } })
      if (!resp.ok) return
      const data = await resp.json()
      const badge = document.getElementById("toolboxChatBadge")
      if (badge) {
        const total = data.total || 0
        badge.textContent = total > 9 ? "9+" : total
        badge.classList.toggle("hidden", total === 0)
        badge.classList.toggle("flex", total > 0)
      }
      // Keep the list rows' bubbles in step when the list is on screen.
      this.element.querySelectorAll(".js-chat-row").forEach(row => {
        const count = (data.rooms || {})[row.dataset.room] || 0
        const pill = row.querySelector(".js-row-badge")
        if (!pill) return
        pill.textContent = count > 99 ? "99+" : count
        pill.classList.toggle("hidden", count === 0)
      })
    } catch { /* fine */ }
  }

  _zeroBadge(room) {
    this.element.querySelectorAll(`.js-chat-row[data-room="${room}"] .js-row-badge`)
        .forEach(pill => pill.classList.add("hidden"))
    this._refreshUnread()
  }

  // ── Rendering ───────────────────────────────────────────────────

  _renderMessages(messages, lastReadAt) {
    this.emptyTarget.classList.toggle("hidden", messages.length > 0)
    let divided = false
    messages.forEach(message => {
      if (!divided && lastReadAt && !message.mine && message.at > lastReadAt) {
        this.listTarget.appendChild(this._newDivider())
        divided = true
      }
      this.listTarget.appendChild(this._bubble(message))
    })
    this._scrollDown(true)
  }

  _newDivider() {
    const el = document.createElement("div")
    el.className = "flex items-center gap-2 text-[10.5px] font-extrabold tracking-widest text-red-500"
    el.innerHTML = `<span class="flex-1 h-px bg-red-300"></span>NEW<span class="flex-1 h-px bg-red-300"></span>`
    return el
  }

  _bubble(message) {
    this.emptyTarget.classList.add("hidden")
    this.lastId = Math.max(this.lastId, message.id || 0)

    const wrap = document.createElement("div")
    wrap.dataset.mid = message.id || ""
    wrap.className = `flex gap-2 max-w-[94%] ${message.mine ? "self-end flex-row-reverse" : ""}`

    const avatar = `<div class="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-[10.5px] font-bold" style="background:${this._color(message.user.id)}">${this._esc(message.user.initials)}</div>`

    let content = ""
    if (message.kind === "image" && message.file) {
      content = `<a href="${message.file.url}" target="_blank"><img src="${message.file.url}" class="rounded-lg max-w-[220px] max-h-[180px] object-cover my-1 border border-gray-200" loading="lazy"></a>`
    } else if (message.kind === "voice" && message.file) {
      const dur = message.duration_seconds ? `${Math.floor(message.duration_seconds / 60)}:${String(message.duration_seconds % 60).padStart(2, "0")}` : ""
      if (Array.isArray(message.waveform) && message.waveform.length) {
        // WhatsApp-style: purple play button + the recorded waveform, bars
        // filling with progress as it plays.
        const bars = message.waveform.map(level =>
          `<span class="js-vbar inline-block w-[3px] rounded-full shrink-0" style="height:${Math.max(4, Math.round(level * 26))}px;background:#b7cdbc"></span>`
        ).join("")
        content = `<span class="js-voice flex items-center gap-2.5 my-0.5 select-none" data-src="${message.file.url}">
          <button type="button" class="js-vplay w-9 h-9 rounded-full bg-[#43b152] hover:bg-[#3ba94a] text-white flex items-center justify-center shrink-0 cursor-pointer">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4.5v15l13-7.5z"/></svg>
          </button>
          <span class="js-vbars flex items-center gap-[2px] h-7 min-w-0 overflow-hidden">${bars}</span>
          <span class="js-vtime text-[11px] text-gray-400 tabular-nums shrink-0">${dur}</span>
        </span>`
      } else {
        content = `<span class="flex items-center gap-2 my-0.5"><audio controls preload="none" src="${message.file.url}" class="h-9 max-w-[210px]"></audio><span class="text-[11px] text-gray-400">${dur}</span></span>`
      }
    } else if (message.kind === "video" && message.file) {
      // The /archive proxy 302s video to a presigned URL, so the browser
      // streams straight from S3 with range support.
      const dur = message.duration_seconds ? `<span class="text-[11px] text-gray-400 tabular-nums block mt-0.5">${Math.floor(message.duration_seconds / 60)}:${String(message.duration_seconds % 60).padStart(2, "0")}</span>` : ""
      content = `<video controls preload="metadata" src="${message.file.url}" class="rounded-lg max-w-[240px] max-h-[200px] my-1 bg-black"></video>${dur}`
    } else if (message.kind === "file" && message.file) {
      content = `<a href="${message.file.url}" target="_blank" class="inline-flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-lg px-2.5 py-1.5 mt-1 text-[12.5px] font-semibold text-gray-700 hover:bg-gray-200"><span class="inline-block w-3.5 h-4 border-[1.6px] border-gray-400 rounded-[3px]"></span>${this._esc(message.file.name)}</a>`
    }
    if (message.body) content = `<span>${this._format(message.body)}</span>` + content

    const company = message.company ? `<span class="font-medium text-gray-400 text-[10px] ml-1">${this._esc(message.company)}</span>` : ""
    // Talk becomes work: hovering any real message offers to carry it into a
    // new Action Item as a pre-filled draft.
    // A photo gets a second chip: file it on the Photos page. Chat photos
    // are NOT project photos until somebody says so (Phelipe 2026-08-21).
    const chip = "bg-white border border-gray-200 rounded-full shadow-sm px-2 py-0.5 text-[10px] font-semibold text-gray-500 hover:text-[#2fa04e] hover:border-[#c9ead0] cursor-pointer whitespace-nowrap"
    const chips = []
    if (message.id) chips.push(`<button type="button" class="js-to-ai ${chip}">→ Action item</button>`)
    if (message.id && message.kind === "image" && message.photo_state === "not_in_photos") {
      chips.push(`<button type="button" class="js-to-photos ${chip}">→ Photos</button>`)
    }
    const toActionItem = chips.length
      ? `<div class="absolute -top-2.5 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">${chips.join("")}</div>`
      : ""
    wrap.innerHTML = `
      ${avatar}
      <div class="group relative border rounded-xl px-3 py-2 text-[13px] leading-relaxed min-w-0 ${message.mine ? "bg-[#dcf8c6] border-[#c3e6ab]" : "bg-white border-gray-200"} ${message.failed ? "opacity-50" : ""}">
        ${toActionItem}
        <div class="text-[11px] font-bold text-gray-700 mb-0.5">${message.mine ? "You" : this._esc(message.user.name)}${company}
          <span class="font-medium text-gray-400 ml-1 text-[10px]">${this._esc(message.at_label || "")}</span>${message.mine && message.id && message.at ? this._tickHtml(message.at) : ""}</div>
        <div class="text-gray-800 break-words">${content}</div>
        ${this._photoLineHtml(message)}
        ${this._mentionEmailHtml(message)}
      </div>`
    this._wireVoicePlayer(wrap, message)
    this._wireMentionEmail(wrap, message)
    this._wireToActionItem(wrap, message)
    this._wirePhotoToggle(wrap, message)
    return wrap
  }

  // Only a FILED photo gets a line under the bubble — it says where the
  // photo went and offers to take it back out. Un-filed photos say nothing;
  // their "→ Photos" chip is the whole offer (Phelipe 2026-08-21).
  _photoLineHtml(message) {
    if (!message.id || message.kind !== "image" || message.photo_state !== "in_photos") return ""
    return `<div class="js-photo-line mt-1 text-[10.5px] font-semibold">${this._photoLineInner("in_photos", message.photo_removable)}</div>`
  }

  _photoLineInner(state, removable) {
    const photosUrl = `/projects/${this.projectIdValue}/photos`
    if (state !== "in_photos") return ""
    const remove = removable
      ? `<button type="button" class="js-photo-toggle text-gray-400 hover:text-red-500 cursor-pointer ml-2">Remove</button>`
      : ""
    return `<span class="text-[#2fa04e] inline-flex items-center gap-1">
              <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M4 12.5l5 5L20 6.5"/></svg>
              Added to <a href="${photosUrl}" class="underline hover:text-[#14572a]">Photos</a></span>${remove}`
  }

  // Both directions run through the same endpoint: the chip files it, the
  // line's Remove pulls it back.
  _wirePhotoToggle(wrap, message) {
    const bubble = wrap.querySelector(".group") || wrap
    bubble.addEventListener("click", async event => {
      const button = event.target.closest(".js-photo-toggle, .js-to-photos")
      if (!button) return
      button.disabled = true
      const adding = button.classList.contains("js-to-photos")
      try {
        const resp = await fetch(this._url("toggle_photo"), {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ message_id: message.id })
        })
        if (!resp.ok) throw new Error("failed")
        const data = await resp.json()
        message.photo_state = data.photo_state
        message.photo_removable = data.photo_removable
        if (adding) {
          button.remove()
          track("Chat Photo Added To Photos", { project_id: this.projectIdValue, room: this.room })
        }
        this._paintPhotoLine(wrap, message)
      } catch {
        button.disabled = false
      }
    })
  }

  // Repaint (or drop) the "Added to Photos" line after a toggle.
  _paintPhotoLine(wrap, message) {
    const bubble = wrap.querySelector(".group") || wrap
    let line = bubble.querySelector(".js-photo-line")
    const html = this._photoLineInner(message.photo_state, message.photo_removable)
    if (!html) { line?.remove(); return }
    if (!line) {
      line = document.createElement("div")
      line.className = "js-photo-line mt-1 text-[10.5px] font-semibold"
      const anchor = bubble.querySelector(".js-memail")
      anchor ? bubble.insertBefore(line, anchor) : bubble.appendChild(line)
    }
    line.innerHTML = html
  }

  _wireToActionItem(wrap, message) {
    const button = wrap.querySelector(".js-to-ai")
    if (!button) return

    button.addEventListener("click", () => {
      const who = message.mine ? "me" : (message.user?.name || "a teammate")
      let core = (message.body || "").trim()
      if (message.kind === "voice" && !core) {
        const s = message.duration_seconds || 0
        core = `Voice note (${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")})`
      }
      if ((message.kind === "file" || message.kind === "image" || message.kind === "video") && message.file) {
        core = [core, `File: ${message.file.name}`].filter(Boolean).join("\n")
      }
      const draft = `${core}\n\n— from ChatLog · ${who} · ${message.at_label || ""}`
      track("Action Item Drafted From Chat", { project_id: this.projectIdValue, kind: message.kind, room: this.room })
      // A photo goes WITH the words (Phelipe 2026-08-21): the composer opens
      // with the picture already attached, no re-uploading from the phone.
      const carry = message.kind === "image" ? `&chatlog_photo_message_id=${message.id}` : ""
      window.location.href = `/projects/${this.projectIdValue}/action_items/new?chatlog_draft=${encodeURIComponent(draft)}${carry}`
    })
  }

  // Tags give a badge only. The sender decides whether an email goes out —
  // this little envelope on their own message is that decision, one shot.
  _mentionEmailHtml(message) {
    if (!message.mine || !message.id || !(message.mentioned_count > 0)) return ""
    if (message.mentions_emailed) {
      return `<div class="js-memail mt-1 text-[10.5px] text-[#2fa04e] font-semibold flex items-center gap-1">
        <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M4 12.5l5 5L20 6.5"/></svg> Emailed</div>`
    }
    return `<div class="js-memail mt-1">
      <button type="button" class="js-memail-btn inline-flex items-center gap-1 text-[10.5px] font-semibold text-gray-400 hover:text-[#2fa04e] cursor-pointer">
        <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
        Email them too?
      </button></div>`
  }

  _wireMentionEmail(wrap, message) {
    const button = wrap.querySelector(".js-memail-btn")
    if (!button) return

    button.addEventListener("click", async () => {
      button.disabled = true
      try {
        const resp = await fetch(this._url("email_mentions"), {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ message_id: message.id })
        })
        if (!resp.ok) throw new Error("failed")
        wrap.querySelector(".js-memail").outerHTML =
          `<div class="js-memail mt-1 text-[10.5px] text-[#2fa04e] font-semibold flex items-center gap-1">
            <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4"><path d="M4 12.5l5 5L20 6.5"/></svg> Emailed</div>`
      } catch {
        button.disabled = false
      }
    }, { once: false })
  }

  // The custom voice player: one hidden audio element per bubble, created on
  // first play. Bars fill purple with progress; the counter runs while
  // playing and falls back to the total when it ends.
  _wireVoicePlayer(wrap, message) {
    const root = wrap.querySelector(".js-voice")
    if (!root) return

    const playButton = root.querySelector(".js-vplay")
    const bars = [...root.querySelectorAll(".js-vbar")]
    const time = root.querySelector(".js-vtime")
    const total = time?.textContent || ""
    const PLAY = '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4.5v15l13-7.5z"/></svg>'
    const PAUSE = '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1.2"/><rect x="14" y="5" width="4" height="14" rx="1.2"/></svg>'
    let audio = null

    playButton.addEventListener("click", () => {
      if (!audio) {
        audio = new Audio(root.dataset.src)
        audio.preload = "auto"
        const duration = () => (isFinite(audio.duration) && audio.duration > 0 ? audio.duration : message.duration_seconds || 1)
        audio.addEventListener("timeupdate", () => {
          const fraction = Math.min(1, audio.currentTime / duration())
          bars.forEach((bar, i) => { bar.style.background = i / bars.length <= fraction ? "#43b152" : "#b7cdbc" })
          const s = Math.floor(audio.currentTime)
          if (time) time.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
        })
        audio.addEventListener("ended", () => {
          playButton.innerHTML = PLAY
          bars.forEach(bar => { bar.style.background = "#b7cdbc" })
          if (time) time.textContent = total
        })
        audio.addEventListener("pause", () => { playButton.innerHTML = PLAY })
        audio.addEventListener("play", () => { playButton.innerHTML = PAUSE })
      }
      audio.paused ? audio.play() : audio.pause()
    })
  }

  _format(text) {
    let html = this._esc(text)
    // Visual highlight for tags — matching what the composer inserted.
    this.participantsAndSelf().forEach(p => {
      const tag = "@" + p.name
      html = html.split(this._esc(tag)).join(`<span class="text-[#43b152] font-semibold">${this._esc(tag)}</span>`)
    })
    return html.replace(/\n/g, "<br>")
  }

  participantsAndSelf() { return this.participants }

  // ── Read receipts (ticks) ───────────────────────────────────────
  // ✓ sent · ✓✓ someone read it · blue ✓✓ everyone read it. Compares the
  // message's time against the other participants' last_seen_at watermarks
  // (only mark_read moves those, so a send can't fake a read).

  _tickState(at) {
    const r = this.receipts || {}
    if (r.all_seen_at && at <= r.all_seen_at) return "all"
    if (r.any_seen_at && at <= r.any_seen_at) return "some"
    return "sent"
  }

  _tickSvg(state) {
    const single = '<svg width="13" height="11" viewBox="0 0 16 12" fill="none"><path d="M1.5 6.5l3.5 3.5L12 3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    const double = '<svg width="16" height="11" viewBox="0 0 19 12" fill="none"><path d="M1.5 6.5l3.5 3.5L12 3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.5 8.8l1.7 1.2L17 3" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    return state === "sent" ? single : double
  }

  _tickHtml(at) {
    const state = this._tickState(at)
    const color = state === "all" ? "#34B7F1" : "#9ca3af"
    return `<span class="js-ticks ml-1 inline-flex align-middle" data-at="${this._esc(at)}" data-state="${state}" style="color:${color}">${this._tickSvg(state)}</span>`
  }

  _paintTicks() {
    this.listTarget.querySelectorAll(".js-ticks").forEach(el => {
      const state = this._tickState(el.dataset.at)
      if (el.dataset.state === state) return
      el.dataset.state = state
      el.style.color = state === "all" ? "#34B7F1" : "#9ca3af"
      el.innerHTML = this._tickSvg(state)
    })
  }

  _color(id) {
    const palette = ["#0e7490", "#43b152", "#b45309", "#0f766e", "#be185d", "#4d7c0f", "#1d4ed8", "#a21caf"]
    return palette[(id || 0) % palette.length]
  }

  _scrollDown(instant) {
    requestAnimationFrame(() => {
      this.listTarget.scrollTo({ top: this.listTarget.scrollHeight, behavior: instant ? "auto" : "smooth" })
    })
  }

  // ── Sending ─────────────────────────────────────────────────────

  inputKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey && this.mentionMenuTarget.classList.contains("hidden")) {
      event.preventDefault()
      this.send()
    }
  }

  async send() {
    if (this.room === "ai") return this._askAi()
    // Mass Text: a post to everyone (asks first), or a reply to one person.
    if (this.room === MASS_TEXT_ROOM) return this.mt.send()
    const body = this.inputTarget.value.trim()
    const attachment = this.pendingAttachment
    if (!body && !attachment) return

    const mentionIds = [...this.mentions.entries()]
      .filter(([name]) => body.includes("@" + name))
      .map(([, id]) => id)

    const payload = {
      room: this.room, body: body,
      kind: attachment ? attachment.kind : "text",
      cache_name: attachment?.cache_name, filename: attachment?.filename,
      duration_seconds: attachment?.duration_seconds,
      mentioned_user_ids: mentionIds
    }

    // Optimistic: it shows up before the server answers.
    const temp = this._bubble({
      id: 0, mine: true, kind: payload.kind, body: body,
      user: { id: this.currentUserIdValue, name: "You", initials: "•" },
      file: attachment ? { name: attachment.filename, url: "#" } : null,
      duration_seconds: attachment?.duration_seconds,
      at_label: "sending…"
    })
    this.listTarget.appendChild(temp)
    this._scrollDown()
    this.inputTarget.value = ""
    this._clearAttachment()
    this.mentions.clear()

    try {
      const resp = await fetch(this._url("create_message"), {
        method: "POST", headers: this._headers(), body: JSON.stringify(payload)
      })
      if (!resp.ok) throw new Error("send failed")
      const data = await resp.json()
      temp.replaceWith(this._bubble(data.message))
    } catch {
      temp.querySelector("div.border")?.classList.add("border-red-300")
      const stamp = temp.querySelector("span.font-medium.text-gray-400.ml-1")
      if (stamp) { stamp.textContent = "failed — tap to retry"; stamp.classList.add("text-red-500") }
      temp.style.cursor = "pointer"
      temp.addEventListener("click", () => {
        temp.remove()
        this.inputTarget.value = body
        this.pendingAttachment = attachment
      }, { once: true })
    }
  }

  // ── Attachments ─────────────────────────────────────────────────

  pickFile() { this.fileInputTarget.click() }

  async fileChosen(event) {
    const file = event.currentTarget.files[0]
    event.currentTarget.value = ""
    if (!file) return
    const isImage = /^image\//.test(file.type)
    const isVideo = /^video\//.test(file.type)
    await this._uploadBlob(file, file.name, file.type, isImage ? "image" : isVideo ? "video" : "file")
  }

  async _uploadBlob(blob, filename, contentType, kind, durationSeconds) {
    this._showAttachment(`Uploading ${filename}…`, true)
    try {
      const presign = await fetch(this._url("presign_uploads"), {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: filename, content_type: contentType || "application/octet-stream", byte_size: blob.size }] })
      })
      if (!presign.ok) throw new Error((await presign.json().catch(() => ({}))).error || "upload refused")
      const upload = (await presign.json()).uploads[0]
      const put = await fetch(upload.upload_url, { method: "PUT", headers: { "Content-Type": upload.content_type }, body: blob })
      if (!put.ok) throw new Error("upload failed")

      this.pendingAttachment = { cache_name: upload.cache_name, filename: upload.filename,
                                 kind: kind, duration_seconds: durationSeconds }
      this._showAttachment(kind === "voice" ? `Voice note ready (${durationSeconds || 0}s) — hit send` : upload.filename, false)
    } catch (error) {
      this._clearAttachment()
      alert(error.message || "Couldn't upload that file.")
    }
  }

  _showAttachment(label, busy) {
    const box = this.element.querySelector(".js-attach-preview")
    box.classList.remove("hidden")
    box.innerHTML = `<span class="inline-flex items-center gap-2 bg-[#dcf8c6] border border-[#c3e6ab] text-[#1d7a35] rounded-lg px-2.5 py-1 text-[12px] font-semibold">
      ${busy ? '<span class="inline-block w-3 h-3 border-2 border-[#43b152] border-t-transparent rounded-full animate-spin"></span>' : ""}
      ${this._esc(label)}
      <button type="button" class="js-attach-remove text-[#43b152] hover:text-[#14572a] font-bold">✕</button></span>`
    box.querySelector(".js-attach-remove").addEventListener("click", () => this._clearAttachment())
  }

  _clearAttachment() {
    this.pendingAttachment = null
    const box = this.element.querySelector(".js-attach-preview")
    box.classList.add("hidden")
    box.innerHTML = ""
  }

  // ── Voice notes (WhatsApp-style) ────────────────────────────────
  // Mic → recorder bar swaps in: live waves + timer, pause/resume, trash to
  // discard, send when it's right. The mic levels sampled while recording
  // ride along with the message so every device draws the same waveform.

  async toggleVoice() {
    // In the AI room the mic asks out loud (speech-to-text) instead of
    // recording a voice note.
    if (this.room === "ai") return this._aiTalkToggle()
    if (this.voiceState && this.voiceState !== "idle") return
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      this.micHintTarget.classList.add("hidden")
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4"
      this.voiceMime = mime
      this.voiceStream = stream
      this.chunks = []
      this.waveSamples = []
      this.voiceSeconds = 0
      this.recorder = new MediaRecorder(stream, { mimeType: mime })
      this.recorder.ondataavailable = e => { if (e.data.size) this.chunks.push(e.data) }
      this.recorder.start(250)
      this.voiceState = "recording"

      // Live level meter → rolling bars on the canvas.
      this.audioCtx = new (window.AudioContext || window.webkitAudioContext)()
      this.analyser = this.audioCtx.createAnalyser()
      this.analyser.fftSize = 512
      this.audioCtx.createMediaStreamSource(stream).connect(this.analyser)
      this.lastSampleAt = 0
      const meter = (ts) => {
        if (!this.voiceState || this.voiceState === "idle") return
        if (this.voiceState === "recording" && ts - this.lastSampleAt > 110) {
          this.lastSampleAt = ts
          const data = new Uint8Array(this.analyser.fftSize)
          this.analyser.getByteTimeDomainData(data)
          let sum = 0
          for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v }
          this.waveSamples.push(Math.min(1, Math.sqrt(sum / data.length) * 3.2))
          this._drawWave()
        }
        this.waveRaf = requestAnimationFrame(meter)
      }
      this.waveRaf = requestAnimationFrame(meter)

      this.voiceTimer = setInterval(() => {
        if (this.voiceState === "recording") {
          this.voiceSeconds += 0.2
          const s = Math.floor(this.voiceSeconds)
          this.recTimerTarget.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
        }
      }, 200)

      this.composerRowTarget.classList.add("hidden")
      this.recorderTarget.classList.remove("hidden")
      this.recorderTarget.classList.add("flex")
      this._paintPauseButton()
      this._drawWave()
    } catch {
      this.micHintTarget.classList.remove("hidden")
      setTimeout(() => this.micHintTarget.classList.add("hidden"), 6000)
    }
  }

  pauseVoice() {
    if (this.voiceState === "recording") {
      this.recorder.pause()
      this.voiceState = "paused"
    } else if (this.voiceState === "paused") {
      this.recorder.resume()
      this.voiceState = "recording"
    }
    this._paintPauseButton()
  }

  cancelVoice() {
    this._teardownVoice()
  }

  async sendVoice() {
    if (!this.recorder || this.voiceState === "idle") return
    const mime = this.voiceMime
    const duration = Math.max(1, Math.round(this.voiceSeconds))
    const waveform = this._downsampleWave(48)

    const blob = await new Promise(resolve => {
      this.recorder.onstop = () => resolve(new Blob(this.chunks, { type: mime }))
      try { this.recorder.stop() } catch { resolve(new Blob(this.chunks, { type: mime })) }
    })
    this._teardownVoice(true)
    if (!blob || blob.size === 0) return

    const ext = mime === "audio/webm" ? "webm" : "mp4"
    const filename = `voice-note.${ext}`
    const localUrl = URL.createObjectURL(blob)

    // Optimistic: the note is playable in the thread before it even uploads.
    const temp = this._bubble({
      id: 0, mine: true, kind: "voice", body: "",
      user: { id: this.currentUserIdValue, name: "You", initials: "•" },
      file: { name: filename, url: localUrl },
      duration_seconds: duration, waveform: waveform, at_label: "sending…"
    })
    this.listTarget.appendChild(temp)
    this._scrollDown()

    try {
      const presign = await fetch(this._url("presign_uploads"), {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: filename, content_type: mime, byte_size: blob.size }] })
      })
      if (!presign.ok) throw new Error("upload refused")
      const upload = (await presign.json()).uploads[0]
      const put = await fetch(upload.upload_url, { method: "PUT", headers: { "Content-Type": upload.content_type }, body: blob })
      if (!put.ok) throw new Error("upload failed")

      const resp = await fetch(this._url("create_message"), {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ room: this.room, kind: "voice", body: "",
                               cache_name: upload.cache_name, filename: upload.filename,
                               duration_seconds: duration, waveform: waveform })
      })
      if (!resp.ok) throw new Error("send failed")
      const data = await resp.json()
      // Keep the local audio playable until the S3 copy is streamed.
      data.message.file.url = localUrl
      temp.replaceWith(this._bubble(data.message))
    } catch {
      const stamp = temp.querySelector("span.font-medium.text-gray-400.ml-1")
      if (stamp) { stamp.textContent = "failed — tap to remove"; stamp.classList.add("text-red-500") }
      temp.style.cursor = "pointer"
      temp.addEventListener("click", () => temp.remove(), { once: true })
    }
  }

  _teardownVoice(keepChunks = false) {
    this.voiceState = "idle"
    cancelAnimationFrame(this.waveRaf)
    clearInterval(this.voiceTimer)
    try { this.voiceStream?.getTracks()?.forEach(t => t.stop()) } catch { /* gone */ }
    try { this.audioCtx?.close() } catch { /* gone */ }
    if (!keepChunks) { try { this.recorder?.state !== "inactive" && this.recorder?.stop() } catch { /* gone */ } }
    this.recorder = keepChunks ? this.recorder : null
    this.audioCtx = null
    this.recTimerTarget.textContent = "0:00"
    this.recorderTarget.classList.add("hidden")
    this.recorderTarget.classList.remove("flex")
    this.composerRowTarget.classList.remove("hidden")
  }

  _paintPauseButton() {
    const paused = this.voiceState === "paused"
    this.pauseButtonTarget.title = paused ? "Resume recording" : "Pause recording"
    this.pauseButtonTarget.innerHTML = paused
      ? '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M7 4.5v15l13-7.5z"/></svg>'
      : '<svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1.2"/><rect x="14" y="5" width="4" height="14" rx="1.2"/></svg>'
    this.recDotTarget.classList.toggle("animate-pulse", !paused)
    this.recDotTarget.classList.toggle("bg-red-500", !paused)
    this.recDotTarget.classList.toggle("bg-gray-300", paused)
  }

  _drawWave() {
    const canvas = this.waveCanvasTarget
    const width = canvas.clientWidth || 200
    if (canvas.width !== width * 2) { canvas.width = width * 2; canvas.height = 48 }
    const ctx = canvas.getContext("2d")
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const barWidth = 4, gap = 3
    const max = Math.floor(canvas.width / (barWidth + gap))
    const samples = this.waveSamples.slice(-max)
    ctx.fillStyle = "#43b152"
    samples.forEach((level, i) => {
      const h = Math.max(4, level * 44)
      const x = (barWidth + gap) * i, y = (48 - h) / 2
      if (ctx.roundRect) {
        ctx.beginPath()
        ctx.roundRect(x, y, barWidth, h, 2)
        ctx.fill()
      } else {
        ctx.fillRect(x, y, barWidth, h)
      }
    })
  }

  _downsampleWave(buckets) {
    const source = this.waveSamples
    if (source.length === 0) return []
    const out = []
    const size = source.length / buckets
    for (let i = 0; i < buckets && i * size < source.length; i++) {
      const slice = source.slice(Math.floor(i * size), Math.max(Math.floor((i + 1) * size), Math.floor(i * size) + 1))
      out.push(Number(Math.max(...slice).toFixed(3)))
    }
    return out
  }

  // ── Mentions ────────────────────────────────────────────────────

  inputChanged() {
    if (this.room === "ai" || this.room === MASS_TEXT_ROOM) { this.mentionMenuTarget.classList.add("hidden"); return }
    const value = this.inputTarget.value
    const caret = this.inputTarget.selectionStart
    const upToCaret = value.slice(0, caret)
    const match = upToCaret.match(/@([\p{L}\p{N}.' -]{0,30})$/u)
    if (!match) { this.mentionMenuTarget.classList.add("hidden"); return }

    const query = match[1].toLowerCase()
    const hits = this.participants.filter(p => p.name.toLowerCase().includes(query)).slice(0, 6)
    if (hits.length === 0) { this.mentionMenuTarget.classList.add("hidden"); return }

    this.mentionMenuTarget.innerHTML = hits.map(p =>
      `<button type="button" class="w-full text-left px-3 py-2 text-[13px] hover:bg-[#dcf8c6] cursor-pointer" data-id="${p.id}" data-name="${this._esc(p.name)}">${this._esc(p.name)}</button>`
    ).join("")
    this.mentionMenuTarget.classList.remove("hidden")
    this.mentionMenuTarget.querySelectorAll("button").forEach(button => {
      button.addEventListener("click", () => this._insertMention(button.dataset.name, parseInt(button.dataset.id), match))
    })
  }

  _insertMention(name, id, match) {
    const input = this.inputTarget
    const caret = input.selectionStart
    const before = input.value.slice(0, caret).replace(/@[\p{L}\p{N}.' -]{0,30}$/u, "@" + name + " ")
    input.value = before + input.value.slice(caret)
    this.mentions.set(name, id)
    this.mentionMenuTarget.classList.add("hidden")
    input.focus()
    input.selectionStart = input.selectionEnd = before.length
  }

  // ── Ask AI ──────────────────────────────────────────────────────
  // A private thread with the project assistant, SAVED server-side (Phelipe
  // 2026-08-24) in the same per-user store the Drawings/Specs assistants
  // keep — so it survives reloads, browsers and devices. sessionStorage is
  // only the instant-paint cache; the server copy wins on load. The finance
  // wall lives inside the assistant service itself.

  _aiKey() { return `chatlog-ai-${this.projectIdValue}` }

  _aiThread() {
    try { return JSON.parse(sessionStorage.getItem(this._aiKey()) || "[]") } catch { return [] }
  }

  _saveAiThread(thread) {
    try { sessionStorage.setItem(this._aiKey(), JSON.stringify(thread.slice(-30))) } catch { /* full */ }
  }

  _renderAiThread() {
    this._paintAiThread(this._aiThread())
    this._refreshAiThread()
  }

  _paintAiThread(thread) {
    this.emptyTarget.textContent = "Ask me anything about this project — schedule, action items, reports, documents, photos. Finance data is off limits."
    this.emptyTarget.classList.toggle("hidden", thread.length > 0)
    if (thread.length) this.listTarget.appendChild(this._aiClearRow())
    thread.forEach(turn => this.listTarget.appendChild(
      this._aiBubble(turn.role, turn.text, turn.sources,
                     { code_refs: turn.code_refs || [], web_query: turn.web_query || null })))
    this._scrollDown(true)
  }

  // The saved thread from the server replaces the cached paint — unless a
  // question is mid-flight or the user already left the AI room.
  async _refreshAiThread() {
    try {
      const resp = await fetch(this._url("ask_ai_history"), { headers: this._headers() })
      if (!resp.ok) return
      const thread = (await resp.json()).thread || []
      if (this.aiBusy || this.room !== "ai" || this.screen !== "chat") return
      this._saveAiThread(thread)
      this.listTarget.innerHTML = ""
      this._paintAiThread(thread.slice(-30))
    } catch { /* offline — the cached paint stands */ }
  }

  // "Clear history" sits above the first bubble — two taps, no browser
  // confirm (house rule), same pattern as leaving a group.
  _aiClearRow() {
    const row = document.createElement("div")
    row.className = "self-center"
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "text-[10.5px] text-gray-400 hover:text-red-500 underline cursor-pointer"
    btn.textContent = "Clear history"
    btn.addEventListener("click", async () => {
      if (!btn.dataset.armed) {
        btn.dataset.armed = "1"
        btn.textContent = "Clear history — sure?"
        setTimeout(() => { btn.dataset.armed = ""; btn.textContent = "Clear history" }, 3000)
        return
      }
      try {
        const resp = await fetch(this._url("clear_ai_history"), { method: "POST", headers: this._headers() })
        if (!resp.ok) throw new Error()
        this._saveAiThread([])
        this.listTarget.innerHTML = ""
        this._paintAiThread([])
      } catch { btn.textContent = "Couldn't clear — try again" }
    })
    row.appendChild(btn)
    return row
  }

  // The answer writes itself out as the model produces it (Phelipe
  // 2026-08-21) — and says what it's digging through on the way. A question
  // that was spoken gets read back out loud.
  async _askAi() {
    const question = this.inputTarget.value.trim()
    if (!question || this.aiBusy) return
    this.aiBusy = true
    this.inputTarget.value = ""
    this.emptyTarget.classList.add("hidden")

    const thread = this._aiThread()
    thread.push({ role: "user", text: question })
    this._saveAiThread(thread)
    this.listTarget.appendChild(this._aiBubble("user", question))

    const bubble = this._aiBubble("model", "")
    const textEl = bubble.querySelector(".js-ai-text")
    this._aiStatus(textEl, "Looking through the project…")
    this.listTarget.appendChild(bubble)
    this._scrollDown()

    const history = thread.slice(-12)
      .map(t => ({ role: t.role === "model" ? "model" : "user", text: t.text }))

    // A spoken question always gets a spoken answer; the speaker toggle does
    // the same for typed ones. Speech rides the STREAM: the first sentence
    // goes to TTS the moment it's typed out, so reading starts near-
    // instantly instead of after the full answer (Phelipe 2026-08-26).
    const wantsVoice = this._speakNextAnswer || this._speakEnabled()
    let pipeline = wantsVoice ? this._startSpeechPipeline() : null
    // Any voiced answer opens with an instant spoken "on it" while the real
    // answer builds — sound starts with the text, not after it.
    if (pipeline) this._queueAck(pipeline)

    let data = null
    try {
      data = await this._streamAi(question, history, textEl, {
        onDelta: (soFar) => this._speechFeed(pipeline, soFar, false),
        // A reset means that text was a false start — take the audio back too.
        onReset: () => { if (pipeline) pipeline = this._startSpeechPipeline() }
      })
    } catch { data = null }
    // A proxy that buffers, an old browser, a dropped stream — the plain
    // endpoint still answers, just all at once instead of live.
    if (!data) {
      try { data = await this._askAiOnce(question, history) } catch { data = null }
    }

    if (data && data.answer) {
      const extras = { code_refs: data.code_refs || [], web_query: data.web_query || null }
      thread.push({ role: "model", text: data.answer, sources: data.sources || [], ...extras })
      this._saveAiThread(thread)
      bubble.replaceWith(this._aiBubble("model", data.answer, data.sources, extras))
      if (pipeline) this._speechFeed(pipeline, data.answer, true)
    } else {
      if (pipeline) this._stopSpeakback()
      bubble.replaceWith(this._aiBubble("model", "I hit a snag answering that — give it another try in a moment."))
    }
    this._speakNextAnswer = false
    this.aiBusy = false
    this._scrollDown()
  }

  // Server-Sent Events over a POST, so it has to be read by hand: frames are
  // separated by a blank line, and a network chunk can cut one in half.
  // Returns the finished answer, or null so the caller can fall back.
  async _streamAi(question, history, textEl, hooks = {}) {
    const resp = await fetch(this._url("ask_ai_stream"), {
      method: "POST",
      headers: { ...this._headers(), "Accept": "text/event-stream" },
      body: JSON.stringify({ question: question, history: history })
    })
    if (!resp.ok || !resp.body) return null

    const reader = resp.body.getReader()
    const decoder = new TextDecoder()
    let buffer = "", answer = "", finished = null

    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let cut
      while ((cut = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, cut)
        buffer = buffer.slice(cut + 2)

        let name = "message", payload = ""
        frame.split("\n").forEach(line => {
          if (line.startsWith("event:")) name = line.slice(6).trim()
          else if (line.startsWith("data:")) payload += line.slice(5).trim()
        })
        if (!payload) continue

        let body
        try { body = JSON.parse(payload) } catch { continue }

        if (name === "step") {
          this._aiStatus(textEl, `${body.text}…`)
        } else if (name === "delta") {
          answer += body.text || ""
          // A caret while it types, so a pause reads as "still writing".
          textEl.innerHTML = this._md(answer) +
            '<span class="inline-block w-1.5 h-3.5 -mb-0.5 ml-0.5 bg-[#53c463] animate-pulse"></span>'
          this._scrollDown()
          hooks.onDelta?.(answer)
        } else if (name === "reset") {
          // That text was a false start — the model went back for a tool.
          answer = ""
          this._aiStatus(textEl, "Still looking…")
          hooks.onReset?.()
        } else if (name === "done") {
          finished = body
        } else if (name === "error") {
          return null
        }
      }
    }
    return finished
  }

  // The original one-shot endpoint, still here as the safety net.
  async _askAiOnce(question, history) {
    const resp = await fetch(this._url("ask_ai"), {
      method: "POST", headers: this._headers(),
      body: JSON.stringify({ question: question, history: history })
    })
    if (!resp.ok) throw new Error("failed")
    return await resp.json()
  }

  // ── Ask out loud + spoken answers (Phelipe 2026-08-26) ─────────────────
  // Speech-to-text stays in the browser (Chrome's SpeechRecognition, which
  // relays audio to Google's speech service — it is NOT on-device). The
  // answer's VOICE is new: server-side Gemini TTS instead of the robot
  // speechSynthesis that got the first version pulled on 08-21.

  _aiTalkToggle() {
    if (this._aiRec) return this._aiTalkStop(true)
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) return
    const rec = new SR()
    this._aiRec = rec
    rec.lang = (document.documentElement.lang || "en").startsWith("pt") ? "pt-BR" : "en-US"
    rec.continuous = true
    rec.interimResults = true
    this._aiHeard = ""
    this._paintAiTalk(true)
    rec.onresult = (e) => {
      let text = ""
      for (const r of e.results) text += r[0].transcript
      this._aiHeard = text.trim()
      if (this.hasInputTarget) this.inputTarget.value = this._aiHeard
      this._armAiSilence(e.results[e.results.length - 1]?.isFinal)
    }
    rec.onerror = () => this._aiTalkStop(false)
    // Chrome ends recognition on its own after long silence — treat a
    // natural end with words on the table as "done, send it".
    rec.onend = () => { if (this._aiRec === rec) this._aiTalkStop(this._aiHeard.length > 0) }
    try { rec.start() } catch (_) { this._aiTalkStop(false) }
  }

  _armAiSilence(utteranceEnded) {
    clearTimeout(this._aiSilenceTimer)
    // A FINAL recognition result means Chrome already heard the utterance
    // end — send almost immediately; otherwise leave room for a breath
    // (was 2.2s; Phelipe 2026-08-26 wants the whole loop near-instant).
    this._aiSilenceTimer = setTimeout(() => this._aiTalkStop(true), utteranceEnded ? 500 : 1400)
  }

  _aiTalkStop(sendIt) {
    clearTimeout(this._aiSilenceTimer)
    const rec = this._aiRec
    this._aiRec = null
    try { rec?.stop() } catch (_) { /* already stopped */ }
    this._paintAiTalk(false)
    const text = (this._aiHeard || "").trim()
    this._aiHeard = ""
    if (sendIt && text && this.room === "ai") {
      if (this.hasInputTarget) this.inputTarget.value = text
      this._speakNextAnswer = true
      track("Chat AI Voice Question Asked", { project_id: this.projectIdValue })
      this._askAi()
    }
  }

  _paintAiTalk(listening) {
    if (this.hasMicButtonTarget) {
      const btn = this.micButtonTarget
      const hidden = btn.classList.contains("hidden")
      const base = "w-9 h-9 rounded-lg border cursor-pointer items-center justify-center shrink-0 " +
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2fa04e]/40 transition-colors"
      // Idle in the AI room the mic wears a soft green invitation;
      // listening flips it SOLID green and pulsing (Phelipe 2026-08-26:
      // green, not red, while recording).
      btn.className = `${hidden ? "hidden" : "flex"} ${base} ${listening
        ? "border-[#2fa04e] bg-[#2fa04e] text-white animate-pulse"
        : "border-[#c9ead0] bg-[#eefaf0] text-[#2fa04e] hover:bg-[#dff5e4]"}`
      btn.title = listening ? "Listening — tap when you're done" : "Ask out loud"
    }
    if (this.hasInputTarget && this.room === "ai") {
      this.inputTarget.placeholder = listening ? "Listening…" : "Ask about this project…"
    }
  }

  toggleSpeakBack() {
    // While an answer is speaking, the button is a stop button first —
    // don't flip the preference out from under a "make it stop" click.
    if (this._speakAudio) { this._stopSpeakback(); return }
    const on = !this._speakEnabled()
    this._speakOn = on
    try { localStorage.setItem("conslog-ai-speak", on ? "1" : "0") } catch (_) {}
    if (!on) this._stopSpeakback()
    this._paintSpeakButton()
  }

  _speakEnabled() {
    if (this._speakOn === undefined) {
      try { this._speakOn = localStorage.getItem("conslog-ai-speak") === "1" } catch (_) { this._speakOn = false }
    }
    return this._speakOn
  }

  // The speech pipeline follows the STREAM: sentences go to TTS as they
  // finish typing, audio pieces play back in order, and the first piece is
  // a single sentence — tiny to synthesize, so speaking starts almost with
  // the text (Phelipe 2026-08-26: "I want it to read instantly").
  _startSpeechPipeline() {
    this._stopSpeakback()
    const state = { gen: (this._speakGen = (this._speakGen || 0) + 1),
                    queue: [], consumed: 0, dispatched: 0, played: 0, done: false }
    return state
  }

  _speechFeed(state, rawText, isFinal) {
    if (!state || state.gen !== this._speakGen) return
    let guard = 24
    while (guard--) {
      const pendingRaw = rawText.slice(state.consumed)
      let take
      if (isFinal) {
        // Final remainder ships in sentence-bounded pieces so one giant
        // answer doesn't become one giant slow TTS request.
        take = pendingRaw.length
        if (take > 1800) {
          const cut = pendingRaw.slice(0, 1800).match(/[\s\S]*[.!?](?=\s)/)
          take = cut ? cut[0].length : 1800
        }
      } else {
        const realPieces = state.dispatched - (state.ackCount || 0)
        const complete = pendingRaw.match(/[\s\S]*[.!?](?=\s)/)
        if (complete) {
          take = complete[0].length
        } else if (realPieces === 0) {
          // The FIRST WORDS go to the voice the instant they exist on
          // screen (Phelipe 2026-08-26: "start talking as soon as the
          // first letter is showing") — the only wait is for one whole
          // word, so the clip never cuts a word in half.
          take = pendingRaw.length <= 60 ? pendingRaw.lastIndexOf(" ") : pendingRaw.lastIndexOf(" ", 60)
          if (take < 4) return
        } else {
          return
        }
        // Piece sizes ramp: tiny opener → quick follow-up → full batches,
        // so the voice never waits on text it doesn't need yet (and a GET
        // query stays a comfortable size).
        const minBatch = realPieces === 0 ? 0 : realPieces === 1 ? 180 : 350
        if (take < minBatch) return
        if (take > 1500) {
          const cut = pendingRaw.slice(0, 1500).match(/[\s\S]*[.!?](?=\s)/)
          take = cut ? cut[0].length : 1500
        }
      }
      if (take <= 0) break
      const chunk = this._plainSpeech(rawText.slice(state.consumed, state.consumed + take))
      state.consumed += take
      if (chunk) {
        // The audio element starts downloading (and can start playing)
        // while Gemini is still rendering the tail of the clip.
        const el = new Audio(this._url("ask_ai_speech_stream") +
          `?text=${encodeURIComponent(chunk)}&persona=${encodeURIComponent(this._voicePersona())}`)
        el.preload = "auto"
        this._speechEnqueue(state, Promise.resolve(el))
      }
      if (!isFinal) return
      if (state.consumed >= rawText.length) break
    }
    if (isFinal) {
      state.done = true
      // Nothing ever made it into the queue — leave the button clean.
      if (state.dispatched === 0) this._paintSpeakButton()
    }
  }

  _speechEnqueue(state, blobPromise) {
    state.dispatched++
    state.queue.push(blobPromise)
    if (!state.playerStarted) {
      state.playerStarted = true
      this._paintSpeakButton("loading")
      this._speechPlayerLoop(state)
    }
  }

  // A pre-rendered "let me take a look" in the assistant's own voice plays
  // the moment a SPOKEN question sends — the first sound lands in about a
  // second while the real answer is still being worked out (Phelipe
  // 2026-08-26: "we need to make it like 1-2 seconds").
  _voicePersona() {
    if (this._voicePersonaVal === undefined) {
      let saved = null
      try { saved = localStorage.getItem("conslog-ai-voice-persona") } catch (_) {}
      this._voicePersonaVal = ["default", "southern", "hillbilly", "redneck", "foreman", "surfer"].includes(saved) ? saved : "default"
    }
    return this._voicePersonaVal
  }

  voicePersonaChanged() {
    if (!this.hasVoicePersonaTarget) return
    const pick = this.voicePersonaTarget.value
    this._voicePersonaVal = ["default", "southern", "hillbilly", "redneck", "foreman", "surfer"].includes(pick) ? pick : "default"
    try { localStorage.setItem("conslog-ai-voice-persona", this._voicePersonaVal) } catch (_) {}
    // Instant preview: the new character introduces itself.
    this._stopSpeakback()
    const state = this._startSpeechPipeline()
    this._queueAck(state)
    state.done = true
  }

  _queueAck(state) {
    const map = this.hasAckUrlsValue ? this.ackUrlsValue : {}
    const urls = map[this._voicePersona()] || map.default || []
    if (!urls.length) return
    const url = urls[Math.floor(Math.random() * urls.length)]
    this._ackCache ||= {}
    if (!this._ackCache[url]) {
      this._ackCache[url] = fetch(url)
        .then(r => (r.ok ? r.blob() : null))
        .then(blob => (blob ? URL.createObjectURL(blob) : null))
        .catch(() => null)
      this._ackCache[url].then(u => { if (!u) delete this._ackCache[url] })
    }
    state.ackCount = 1
    this._speechEnqueue(state, this._ackCache[url].then(u => (u ? new Audio(u) : null)))
  }

  async _speechPlayerLoop(state) {
    while (state.gen === this._speakGen) {
      const pending = state.queue[state.played]
      if (!pending) {
        if (state.done) break
        await new Promise(resolve => setTimeout(resolve, 60))
        continue
      }
      state.played++
      const audioEl = await pending
      if (state.gen !== this._speakGen) return
      if (audioEl) await this._playSpeech(audioEl)
    }
    if (state.gen === this._speakGen) this._paintSpeakButton()
  }

  _playSpeech(audio) {
    return new Promise(resolve => {
      audio.playbackRate = this._speakRate()
      try { audio.preservesPitch = true } catch (_) { /* older engines */ }
      this._speakAudio = audio
      audio.onended = audio.onerror = () => {
        if (this._speakAudio === audio) this._speakAudio = null
        resolve()
      }
      this._paintSpeakButton("playing")
      audio.play().catch(() => resolve())
    })
  }

  _stopSpeakback() {
    this._speakGen = (this._speakGen || 0) + 1
    const audio = this._speakAudio
    this._speakAudio = null
    try { audio?.pause() } catch (_) { /* already stopped */ }
    this._paintSpeakButton()
  }

  // Speaking speed: 1 → 1.2 → 1.35 → 1.5 → 2, remembered, applied live
  // mid-play. Default 1.35 (Phelipe 2026-08-26: "a little faster").
  _speakRate() {
    if (this._speakRateVal === undefined) {
      let saved = null
      try { saved = parseFloat(localStorage.getItem("conslog-ai-speak-rate")) } catch (_) {}
      this._speakRateVal = [1, 1.2, 1.35, 1.5, 2].includes(saved) ? saved : 1.35
    }
    return this._speakRateVal
  }

  cycleSpeakRate() {
    const order = [1, 1.2, 1.35, 1.5, 2]
    const next = order[(order.indexOf(this._speakRate()) + 1) % order.length]
    this._speakRateVal = next
    try { localStorage.setItem("conslog-ai-speak-rate", String(next)) } catch (_) {}
    if (this._speakAudio) this._speakAudio.playbackRate = next
    this._paintSpeakRate()
  }

  // The persona picker rides with the speed chip: visible only in the AI
  // room once Voice is ON (Phelipe 2026-08-26 screenshot — it was landing
  // in the top row next to the input).
  _paintVoicePersona() {
    if (!this.hasVoicePersonaTarget) return
    const el = this.voicePersonaTarget
    const show = this.room === "ai" && this._speakEnabled()
    el.classList.toggle("hidden", !show)
    if (show) el.value = this._voicePersona()
  }

  _paintSpeakRate() {
    if (!this.hasSpeakRateButtonTarget) return
    const btn = this.speakRateButtonTarget
    // The speed chip only exists while voice answers are on — one less
    // mystery button the rest of the time.
    const show = this.room === "ai" && this._speakEnabled()
    btn.classList.toggle("hidden", !show)
    btn.classList.toggle("flex", show)
    btn.textContent = `${this._speakRate()}×`
  }

  // What gets read aloud: the answer minus markdown noise, capped so a long
  // report summary doesn't monologue for minutes.
  _plainSpeech(text) {
    return (text || "")
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/^\s*[•*-]\s+/gm, "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 4000)
  }

  _paintSpeakButton(state) {
    if (!this.hasSpeakButtonTarget) return
    const btn = this.speakButtonTarget
    const hidden = btn.classList.contains("hidden")
    const on = this._speakEnabled()
    const busy = state === "playing" || state === "loading"
    const base = "h-9 px-3 rounded-lg border text-[12px] font-medium cursor-pointer items-center gap-1.5 shrink-0 " +
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2fa04e]/40 transition-colors"
    btn.className = `${hidden ? "hidden" : "flex"} ${base} ${(on || busy)
      ? "bg-[#2fa04e] border-[#2fa04e] text-white hover:bg-[#3ba94a]"
      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"}${state === "playing" ? " animate-pulse" : ""}`
    const label = btn.querySelector(".js-speak-label")
    if (label) {
      label.textContent = state === "playing" ? "Speaking…"
        : state === "loading" ? "Voice…"
        : on ? "Voice on" : "Voice off"
    }
    btn.title = state === "playing" ? "Speaking — click to stop"
      : on ? "Spoken answers are on" : "Spoken answers are off"
    this._paintSpeakRate()
    this._paintVoicePersona()
  }

  _aiStatus(textEl, label) {
    textEl.innerHTML =
      '<span class="inline-flex items-center gap-2 text-gray-400"><span class="inline-block w-3.5 h-3.5 border-2 border-[#53c463] border-t-transparent rounded-full animate-spin"></span>' +
      this._esc(label) + "</span>"
  }


  _aiBubble(role, text, sources = [], extras = {}) {
    const wrap = document.createElement("div")
    const mine = role === "user"
    wrap.className = `flex gap-2 max-w-[94%] ${mine ? "self-end flex-row-reverse" : ""}`

    const avatar = mine
      ? `<div class="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-[10.5px] font-bold" style="background:${this._color(this.currentUserIdValue)}">You</div>`
      : `<div class="w-7 h-7 rounded-full shrink-0 flex items-center justify-center bg-[#53c463] text-white"><svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.8 5.7L19.5 9l-5.7 1.8L12 16.5l-1.8-5.7L4.5 9l5.7-1.3z"/></svg></div>`

    // Sources organized like the Drawings/Specs Ask AI (Phelipe 2026-08-24):
    // one row per document — repeat citations fold into one row with their
    // pages listed — and clicking filters the docs list to that document
    // without opening the PDF. External file links still open fresh.
    const sourceLine = !mine && sources && sources.length
      ? `<div class="mt-1.5 pt-1.5 border-t border-dashed border-gray-200">
           <div class="text-[10.5px] font-semibold text-gray-400 mb-0.5">Sources</div>
           ${this._sourceSections(sources)}
         </div>`
      : ""

    // Codes the answer named — links come from OUR trusted-source map, never
    // from the model. Same external block the Drawings/Specs assistant shows.
    const codeRefs = extras.code_refs || []
    const codeLine = !mine && codeRefs.length
      ? `<div class="mt-1.5 pt-1.5 border-t border-dashed border-gray-200 text-[10.5px] text-gray-500">
           <span class="font-semibold text-gray-400">Codes &amp; standards</span> ${codeRefs.map(c =>
             `<a href="${this._esc(c.url)}" target="_blank" rel="noopener" class="underline hover:text-[#2fa04e]">${this._esc(c.name)}</a>`
           ).join(" · ")}
           <div class="text-gray-400 mt-0.5">Official text and local amendments govern.</div>
         </div>`
      : ""

    // Anything the project's own documents couldn't settle can be checked
    // against the web, grounded in Google Search.
    const webBtn = !mine && extras.web_query
      ? `<div class="js-web-wrap mt-1.5">
           <button type="button" class="js-web-btn text-[10.5px] font-semibold text-[#2fa04e] hover:text-[#14572a] cursor-pointer underline">Check the web</button>
         </div>`
      : ""

    wrap.innerHTML = `
      ${avatar}
      <div class="border rounded-xl px-3 py-2 text-[13px] leading-relaxed min-w-0 ${mine ? "bg-[#dcf8c6] border-[#c3e6ab]" : "bg-white border-gray-200"}">
        <div class="js-ai-text text-gray-800 break-words whitespace-pre-wrap">${this._md(text)}</div>
        ${sourceLine}
        ${codeLine}
        ${webBtn}
      </div>`
    if (webBtn) this._wireWebLookup(wrap, extras.web_query)
    // Document mentions inside the answer become links once the doc-links
    // map is in (Larry 2026-08-25: "whenever they reference any type of
    // document, make those hyperlinks").
    if (!mine && text) {
      this._loadDocLinks().then(() => this._linkifyDocRefs(wrap.querySelector(".js-ai-text")))
    }
    return wrap
  }

  // Every current sheet/section number with its locate link — fetched once
  // per panel life, shared by every AI bubble.
  _loadDocLinks() {
    if (this._docLinksPromise) return this._docLinksPromise
    this._docLinksPromise = fetch(this._url("ask_ai_doc_links"), { headers: this._headers() })
      .then(r => (r.ok ? r.json() : { links: [] }))
      .then(data => {
        // Longest token first so "C-3.02" wins over a plain "C-3".
        this._docLinks = (data.links || []).filter(l => l.token && l.url)
          .sort((a, b) => b.token.length - a.token.length)
      })
      .catch(() => { this._docLinks = [] })
    return this._docLinksPromise
  }

  _linkifyDocRefs(el) {
    const links = this._docLinks
    if (!el || !links || !links.length) return
    let html = el.innerHTML
    let changed = false
    links.forEach(l => {
      const esc = l.token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      // Tokens with letters ("SD-401", "C-3.02") match on their own; bare
      // numbers only right after "Section"/"SD" so "401 tons" stays text.
      const re = /[a-z]/i.test(l.token)
        ? new RegExp(`(^|[^\\w/=-])(${esc})(?![\\w.-])`, "g")
        : new RegExp(`((?:Section|SD)[\\s-]*)(${esc})(?![\\w.-])`, "gi")
      html = html.replace(re, (m, pre, tok) => {
        changed = true
        return `${pre}<a href="${l.url}" class="underline decoration-dotted text-[#2fa04e] hover:text-[#14572a]">${tok}</a>`
      })
    })
    if (changed) el.innerHTML = html
  }

  // "Check the web" — one shot per answer, result lands under the button.
  _wireWebLookup(wrap, query) {
    const button = wrap.querySelector(".js-web-btn")
    const box = wrap.querySelector(".js-web-wrap")
    if (!button || !box) return

    button.addEventListener("click", async () => {
      button.disabled = true
      button.textContent = "Checking the web…"
      try {
        const resp = await fetch(this._url("web_lookup"), {
          method: "POST", headers: this._headers(), body: JSON.stringify({ query })
        })
        const data = await resp.json()
        if (!resp.ok) throw new Error(data.error || "failed")
        const links = (data.links || []).map(l =>
          `<a href="${this._esc(l.url)}" target="_blank" rel="noopener" class="underline hover:text-[#2fa04e]">${this._esc(l.title)}</a>`
        ).join(" · ")
        box.innerHTML = `<div class="pt-1.5 border-t border-dashed border-gray-200">
            <div class="text-[10.5px] font-semibold text-gray-400">From the web</div>
            <div class="text-[13px] text-gray-700 whitespace-pre-wrap">${this._md(data.answer || "")}</div>
            ${links ? `<div class="text-[10.5px] text-gray-400 mt-1">${links}</div>` : ""}
          </div>`
        track("Chat AI Web Lookup", { project_id: this.projectIdValue })
      } catch (err) {
        button.disabled = false
        button.textContent = "Check the web"
        this._toast?.(err.message)
      }
      this._scrollDown()
    })
  }

  // The two document sets sit side by side — a Drawings column and a Specs
  // column — so it's plain which sheets and which sections backed the answer
  // (Phelipe 2026-08-24). Everything else (reports, action items…) lists
  // underneath. Set headers only appear when documents were cited at all.
  _sourceSections(sources) {
    const groups = this._groupSources(sources)
    const drawings = groups.filter(g => g.set === "drawings")
    const specs = groups.filter(g => g.set === "specs")
    const rest = groups.filter(g => !g.set)
    const rows = list => list.map(g => this._sourceRow(g)).join("")

    let html = ""
    if (drawings.length || specs.length) {
      const col = (title, list) => list.length
        ? `<div class="flex-1 min-w-[130px]">
             <div class="text-[10px] font-semibold uppercase tracking-wide text-gray-400">${title}</div>
             ${rows(list)}
           </div>`
        : ""
      html += `<div class="flex flex-wrap gap-x-4 gap-y-1">${col("Drawings", drawings)}${col("Specs", specs)}</div>`
    }
    html += rows(rest)
    return html
  }

  _sourceRow(g) {
    const pages = g.pages.length ? `<span class="text-gray-400">, p.${g.pages.join(" · p.")}</span>` : ""
    const label = `${this._esc(g.label)}${pages}`
    // pdfUrl → the same floating page-1 hover preview the Drawings/Specs
    // lists use (Larry 2026-08-25).
    const thumb = g.pdfUrl
      ? ` data-chat-thumb data-pdf-url="${this._esc(g.pdfUrl)}" data-thumb-label="${this._esc(g.label)}"`
      : ""
    if (!g.url) return `<div class="text-[10.5px] leading-4 text-gray-500"${thumb}>${label}</div>`
    const blank = /^https?:/.test(g.url) ? ' target="_blank" rel="noopener"' : ""
    return `<div class="text-[10.5px] leading-4"${thumb}><a href="${this._esc(g.url)}"${blank} class="text-gray-500 hover:text-[#2fa04e]"><span class="underline">⌕ ${label}</span></a></div>`
  }

  // Fold repeat citations of one document into a single row, its cited
  // pages collected in order. Non-document sources group by their label.
  _groupSources(sources) {
    const groups = []
    const byKey = {}
    sources.forEach(s => {
      const label = s.label || this._sourceLabel({ ...s, page: null })
      const key = `${s.type || ""}|${s.id || label}`
      let g = byKey[key]
      if (!g) {
        const set = s.type === "drawings_spec" ? "drawings" : s.type === "specification" ? "specs" : null
        g = byKey[key] = { label: label.replace(/, p\.\d+$/, ""), url: s.url || null,
                           pdfUrl: s.pdf_url || null, pages: [], set }
        groups.push(g)
      }
      if (s.page && !g.pages.includes(s.page)) g.pages.push(s.page)
    })
    return groups
  }

  _sourceLabel(source) {
    // Plan sheets and spec sections cite themselves properly: "Sheet A-101, p.3".
    if (source.label) return source.page ? `${source.label}, p.${source.page}` : source.label
    switch (source.type) {
      case "action_item": return source.code || `action item #${source.id}`
      case "daily_report": return `report ${source.date || `#${source.id}`}`.trim()
      case "document": return source.filename || "document"
      case "document_folder": return "documents"
      case "drawings_spec": return "drawings & specs"
      case "specification": return "specifications"
      case "closeout_item": return "closeout"
      case "schedule": return "schedule"
      case "photo": return "photos"
      case "project": return "project info"
      default: {
        const type = (source.type || "").replace(/_/g, " ")
        return type || "project"
      }
    }
  }

  // Just enough markdown for AI answers: **bold** and tidy bullets. Escaped
  // first, so nothing the model writes can inject markup.
  _md(text) {
    return this._esc(text)
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/^\s*\*\s+/gm, "• ")
  }

  // ── Plumbing ────────────────────────────────────────────────────

  _url(action) { return `/projects/${this.projectIdValue}/chat/${action}` }

  _headers() {
    return { "Content-Type": "application/json", "Accept": "application/json",
             "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
  }

  // Safe in BOTH text and attribute position — quotes must be escaped too,
  // or a value carrying an inch mark (`6" VERTICAL CURB`) closes its own
  // attribute and swallows the rest of the tag, data-* attributes included.
  _esc(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : String(value)
    return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
